window.__subscriptionPageLoaded = true;
(function () {
  const productCache = new Map();
  const lifecycleImageFetchCache = new Map();
  const subscriptionProductFetchCache = new Map();
  const handleResolutionCache = new Map();
  let modal = null;
  let activeCard = null;
  let activeProductFilter = 'VIEW ALL';
  let skioReadyObserver = null;
  let skioMutationObserver = null;
  let filterBlockVisibilityObserver = null;
  let purchaseModeOverride = null;
  let syncCartVariantDiscountBadges = () => {};

  const SKIO_ROOT_SELECTOR = '#skio-root';
  const SKIO_CART_SELECTOR = '[data-skio-name="cart"]';
  const SKIO_MOBILE_CART_SELECTOR = '[data-skio-name="bab-cart-mobile"]';
  const SKIO_MOBILE_CART_HEADER_SELECTOR = '[data-skio-name="bab-cart-mobile-header"]';
  const SKIO_MOBILE_CART_TITLE_ROW_SELECTOR = '[data-skio-name="bab-cart-mobile-title-row"]';
  const SKIO_PRODUCT_GRID_SELECTOR = '[data-skio-name="dynamic-box-product-grid"]';
  const SKIO_UPSELL_GRID_SELECTOR = '[data-skio-name="dynamic-box-upsell-grid"]';
  const SKIO_CARD_SELECTOR = '[data-skio-name="product-card"]';
  const SKIO_CART_UPSELL_SECTION_SELECTOR = '[data-skio-name="bab-cart-upsell-section"]';
  const SKIO_CART_UPSELL_CARD_SELECTOR = '[data-skio-name="bab-cart-upsell"]';
  const UPSELL_GRID_CLASS = 'subscription-upsell-grid';
  const SUBSCRIPTION_OPTION_SELECTOR = '[data-skio-name="bab-cart-frequency-option-subscription"]';
  const ONETIME_OPTION_SELECTOR = '[data-skio-name="bab-cart-frequency-option-onetime"]';
  const FREQUENCY_CONTENT_SELECTOR = '[data-skio-name="bab-cart-frequency-content"]';
  const PURCHASE_MODE_ICON_CLASS = 'subscription-purchase-mode-icon';
  const MOBILE_INSTRUCTIONS_SELECTOR = '[data-subscription-mobile-instructions="true"]';
  const TITLE_SELECTORS = [
    '[data-skio-name="product-card-title"]',
    '[data-skio-name="product-title"]',
  ];
  const ADD_TO_BOX_BUTTON =
    '[data-skio-name="product-card-add-button"], [data-skio-name="add-to-box-button"], [data-skio-name="bab-cart-add-button"]';
  const QUANTITY_SELECTOR = '[data-skio-name="quantity-selector"]';
  // Skio's own explicit, stable attribute for its native decrease button —
  // confirmed live against production markup. Clicking this button at
  // quantity 1 triggers a full document reload (not an in-place AJAX
  // update) inside Skio's app; under real-world network/script load that
  // reload can fail partway through and land on Skio's own error boundary.
  // Detecting via this explicit attribute (rather than positional/first-
  // child heuristics, or waiting for our own debounced sync pass to swap in
  // the proxy stepper) lets the click guard intercept it immediately, with
  // no dependency on timing.
  const NATIVE_QUANTITY_DECREASE_SELECTOR = '[data-skio-name="quantity-selector-decrease-button"]';
  const CART_VARIANT_QUANTITY_DECREASE_SELECTOR = '[data-skio-name="bab-cart-variant-quantity-decrease"]';
  const CART_VARIANT_QUANTITY_INCREASE_SELECTOR = '[data-skio-name="bab-cart-variant-quantity-increase"]';
  const PROXY_STEPPER_ACTION_SELECTOR = '[data-subscription-stepper-action]';
  const CARD_PROXY_STEPPER_SELECTOR = '[data-subscription-card-stepper="true"]';
  const PRODUCT_CARD_ADD_LABEL = 'ADD TO RITUAL';
  const ADDED_CTA_LABEL = 'ADDED';

  function formatAddedCtaLabel(quantity) {
    return quantity > 0 ? `${ADDED_CTA_LABEL} ${quantity}` : ADDED_CTA_LABEL;
  }
  const MOBILE_STEPPER_SELECTOR = '[data-subscription-mobile-stepper="true"]';
  const DESKTOP_STEPPER_SELECTOR = '[data-subscription-desktop-stepper="true"]';
  const FILTER_EMPTY_STATE_SELECTOR = '[data-subscription-filter-empty-state="true"]';
  const FILTER_EMPTY_GRID_CLASS = 'subscription-filter-grid-empty';
  const QTY_ROW_SELECTOR = '[data-subscription-qty-row="true"]';
  const STEPPER_TIERS = [
    { threshold: 1, badge: '1', label: '1 product' },
    { threshold: 2, badge: '2', label: '2 products' },
    { threshold: 3, badge: '3', label: '3+ products' },
  ];
  const MOBILE_STEPPER_TIERS = STEPPER_TIERS;
  const CART_MAX_SLOTS = 4;

  function init() {
    ensureModal();
    setupProductFilters();
    setupSkioEnhancements();
  }

  // Skio's cart widget is a client-rendered Next.js app. Whenever a render
  // throws inside it (whatever the trigger — a DOM mismatch from anything
  // mutating its tree externally, a transient fetch failure, a Skio-side
  // bug), its own error boundary swaps the whole widget for a generic
  // "Failed to load page" / "Try again" fallback. We can't guarantee we've
  // found and fixed every way that gets triggered, and Skio's boundary
  // gives no reliable way to distinguish "will recover if retried" from
  // "won't" ahead of time — so instead of only trying to prevent the crash,
  // detect the fallback screen itself by its exact copy (it has no stable
  // selector/class to hook) and recover from it automatically, before a
  // person ever has to look at it or click anything themselves.
  function setupSkioErrorRecoveryWatchdog() {
    if (document.documentElement.dataset.subscriptionErrorWatchdogAttached === 'true') {
      return;
    }

    // Guard against running before document.body exists (e.g. if this ever
    // gets invoked earlier than the deferred/init-time call this file
    // normally makes). observer.observe(null, ...) throws synchronously
    // with no console output if body isn't there yet, which would silently
    // disable the entire watchdog.
    if (!document.body) {
      document.addEventListener('DOMContentLoaded', setupSkioErrorRecoveryWatchdog, { once: true });
      return;
    }

    document.documentElement.dataset.subscriptionErrorWatchdogAttached = 'true';

    const RETRY_COOLDOWN_MS = 1500;
    const MAX_AUTO_RETRIES = 3;
    // A page-level recovery (reload/navigate) resets all in-memory state on
    // the next load, including retryCount — so a persistently broken route
    // (not a transient glitch) would otherwise make this reload forever,
    // visible as a repeating "redirect" with no console output. Cap total
    // page-level recoveries within a rolling window using sessionStorage,
    // which survives the reload, so it actually stops instead of looping.
    const STORAGE_KEY = 'subscriptionErrorRecovery';
    const RECOVERY_WINDOW_MS = 60000;
    const MAX_PAGE_RECOVERIES = 2;
    const BASE_PATH = '/a/account/build-a-box';

    let retryCount = 0;
    let lastRetryAt = 0;
    let pageRecoveryUsed = false;

    function readRecoveryState() {
      try {
        const raw = sessionStorage.getItem(STORAGE_KEY);
        if (!raw) {
          return { count: 0, firstAt: 0 };
        }
        const parsed = JSON.parse(raw);
        if (!parsed.firstAt || Date.now() - parsed.firstAt > RECOVERY_WINDOW_MS) {
          return { count: 0, firstAt: 0 };
        }
        return parsed;
      } catch (error) {
        return { count: 0, firstAt: 0 };
      }
    }

    function recordPageRecovery() {
      try {
        const state = readRecoveryState();
        const next = { count: state.count + 1, firstAt: state.firstAt || Date.now() };
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(next));
        return next.count;
      } catch (error) {
        // sessionStorage unavailable — fail safe by refusing to recover
        // rather than risk looping with no way to track attempts.
        return MAX_PAGE_RECOVERIES + 1;
      }
    }

    function findSkioErrorBoundary() {
      const heading = [...document.querySelectorAll('h1, h2, h3, [role="heading"]')].find((node) =>
        /failed to load page/i.test(node.textContent || '')
      );

      if (!heading) {
        return null;
      }

      const scope = heading.closest('body > *') || document.body;
      const retryButton = [...scope.querySelectorAll('button, [role="button"]')].find((node) =>
        /try again/i.test(node.textContent || '')
      );

      return { heading, scope, retryButton };
    }

    // Verified live (screen recording, frame-by-frame) that Skio's error
    // boundary renders in place — no navigation — and can take over a
    // second before its own "Try Again" click leads anywhere. Clicking
    // retry alone still leaves the raw "Failed to load page" copy on
    // screen for that whole stretch. Masking it the instant it's detected
    // (same tick as the mutation) means nobody ever actually sees Skio's
    // fallback text, regardless of how long its own recovery takes.
    function maskErrorBoundary(scope) {
      if (!scope || scope.dataset.subscriptionErrorMasked === 'true') {
        return;
      }

      scope.dataset.subscriptionErrorMasked = 'true';
      scope.style.visibility = 'hidden';

      const overlay = document.createElement('div');
      overlay.dataset.subscriptionErrorOverlay = 'true';
      overlay.setAttribute('aria-hidden', 'true');
      overlay.style.cssText =
        'position:fixed;inset:0;background:#fff;z-index:2147483647;display:flex;align-items:center;justify-content:center;';
      overlay.innerHTML =
        '<div style="width:32px;height:32px;border:3px solid #e5e5e5;border-top-color:#2a6553;border-radius:50%;animation:subscriptionErrorMaskSpin 0.8s linear infinite;"></div>' +
        '<style>@keyframes subscriptionErrorMaskSpin{to{transform:rotate(360deg)}}</style>';
      document.body.appendChild(overlay);
    }

    function unmaskErrorBoundary() {
      document.querySelectorAll('[data-subscription-error-overlay="true"]').forEach((el) => el.remove());
      document.querySelectorAll('[data-subscription-error-masked="true"]').forEach((el) => {
        delete el.dataset.subscriptionErrorMasked;
        el.style.visibility = '';
      });
    }

    function attemptRecovery() {
      const boundary = findSkioErrorBoundary();
      if (!boundary) {
        retryCount = 0;
        unmaskErrorBoundary();
        return;
      }

      maskErrorBoundary(boundary.scope);

      const now = Date.now();
      if (now - lastRetryAt < RETRY_COOLDOWN_MS) {
        return;
      }
      lastRetryAt = now;

      if (retryCount < MAX_AUTO_RETRIES && boundary.retryButton) {
        retryCount += 1;
        boundary.retryButton.click();
        return;
      }

      if (pageRecoveryUsed) {
        return;
      }
      pageRecoveryUsed = true;

      if (recordPageRecovery() > MAX_PAGE_RECOVERIES) {
        // Already attempted a full reload/navigate this many times in the
        // last minute and it's still broken — stop here rather than risk
        // an infinite loop. Leave the real fallback screen visible.
        return;
      }

      // A dynamic per-box URL can end up with nothing valid to render (e.g.
      // its last item was just removed) — reloading that same URL can't
      // fix that, so navigate back to the non-dynamic base route instead.
      // Only fall back to a plain reload once we're already there.
      if (window.location.pathname !== BASE_PATH && window.location.pathname.indexOf(BASE_PATH) === 0) {
        window.location.href = BASE_PATH;
      } else {
        window.location.reload();
      }
    }

    const observer = new MutationObserver(() => attemptRecovery());
    observer.observe(document.body, { childList: true, subtree: true, characterData: true });

    window.setInterval(attemptRecovery, RETRY_COOLDOWN_MS);
  }

  function setTextContentIfChanged(element, value) {
    if (!element) {
      return;
    }

    const nextValue = String(value ?? '');
    if (element.textContent !== nextValue) {
      element.textContent = nextValue;
    }
  }

  function setInnerHtmlIfChanged(element, html) {
    if (!element) {
      return;
    }

    const nextValue = String(html ?? '');
    if (element.dataset.subscriptionHtml !== nextValue) {
      element.innerHTML = nextValue;
      element.dataset.subscriptionHtml = nextValue;
    }
  }

  function setHiddenIfChanged(element, isHidden) {
    if (!element) {
      return;
    }

    if (element.hidden !== isHidden) {
      element.hidden = isHidden;
    }
  }

  function markCardGroup(element, label = 'product') {
    if (!element || element.dataset.subscriptionCardGroupApplied === 'true') {
      return;
    }

    const target = element.tagName === 'LI' ? element.firstElementChild : element;
    if (!target) {
      return;
    }

    target.setAttribute('role', 'group');
    target.setAttribute('aria-label', label);
    target.dataset.subscriptionCardGroupApplied = 'true';
  }

  function getCartUpsellCardFromControl(section, control) {
    let node = control?.parentElement;

    while (node && node !== section) {
      if (
        node.querySelector('img') &&
        node.querySelector('button, [role="button"], select') &&
        node.textContent.trim()
      ) {
        return node;
      }

      node = node.parentElement;
    }

    return null;
  }

  function stripStaticCartUpsellCarouselMarkup() {
    document.querySelectorAll(SKIO_CART_UPSELL_SECTION_SELECTOR).forEach((section) => {
      const candidates = [
        section,
        ...section.querySelectorAll('[aria-label], [aria-roledescription], [data-accessible-carousel]'),
      ];

      candidates.forEach((element) => {
        const ariaLabel = element.getAttribute('aria-label') || '';
        const roleDescription = element.getAttribute('aria-roledescription') || '';
        const role = element.getAttribute('role') || '';
        const hasCarouselLabel =
          /carousel/i.test(ariaLabel) ||
          /carousel/i.test(roleDescription);

        if (hasCarouselLabel) {
          element.removeAttribute('aria-label');
          element.removeAttribute('aria-roledescription');
        }

        if (role === 'region' && hasCarouselLabel) {
          element.removeAttribute('role');
        }

        if (element.hasAttribute('data-accessible-carousel')) {
          element.removeAttribute('data-accessible-carousel');
        }
      });

      section
        .querySelectorAll('[role="group"], [aria-label]')
        .forEach((element) => {
          const label = element.getAttribute('aria-label') || '';

          if (/slide\s+\d+/i.test(label)) {
            element.removeAttribute('role');
            element.removeAttribute('aria-label');
          }
        });

      section
        .querySelectorAll('.visually-hidden, .sr-only, [class*="screen-reader"]')
        .forEach((element) => {
          const text = element.textContent.trim();

          if (/carousel|slide|arrow/i.test(text)) {
            element.hidden = true;
            element.setAttribute('aria-hidden', 'true');
          }
        });
    });
  }

  function markCartUpsellCards() {
    document.querySelectorAll(SKIO_CARD_SELECTOR).forEach((card) => {
      markCardGroup(card);
    });

    document.querySelectorAll(SKIO_CART_UPSELL_CARD_SELECTOR).forEach((card) => {
      markCardGroup(card);
    });

    document.querySelectorAll(SKIO_CART_UPSELL_SECTION_SELECTOR).forEach((section) => {
      section
        .querySelectorAll('button, [role="button"], select')
        .forEach((control) => {
          markCardGroup(getCartUpsellCardFromControl(section, control));
        });
    });
  }

  function wrapProductCardCollectionsInLists() {
    const unwrappedCards = [...document.querySelectorAll(SKIO_CARD_SELECTOR)].filter((card) => {
      return !card.closest('.subscription-product-card-list-item');
    });
    const cardsByParent = new Map();

    unwrappedCards.forEach((card) => {
      const parent = card.parentElement;
      if (!parent) {
        return;
      }

      if (!cardsByParent.has(parent)) {
        cardsByParent.set(parent, []);
      }

      cardsByParent.get(parent).push(card);
    });

    cardsByParent.forEach((cards, parent) => {
      if (cards.length === 0) {
        return;
      }

      if (parent.tagName === 'UL' || parent.tagName === 'OL') {
        cards.forEach((card) => {
          if (card.parentElement?.tagName === 'LI') {
            return;
          }

          const listItem = document.createElement('li');
          listItem.className = 'subscription-product-card-list-item';
          card.before(listItem);
          listItem.appendChild(card);
        });
        return;
      }

      const list = document.createElement('ul');
      list.className = 'subscription-product-card-list';
      list.setAttribute('aria-label', 'products');

      parent.insertBefore(list, cards[0]);

      cards.forEach((card) => {
        const listItem = document.createElement('li');
        listItem.className = 'subscription-product-card-list-item';
        list.appendChild(listItem);
        listItem.appendChild(card);
      });
    });
  }

  function syncProductCardCtaLayout() {
    document.querySelectorAll(SKIO_CARD_SELECTOR).forEach((card) => {
      const ctaGroup =
        card.querySelector('[data-skio-name="product-card-actions"]') ||
        card.querySelector('[data-skio-name="product-card-footer"]');
      const buttonContainer = card.querySelector('[data-skio-name="product-card-button-container"]');
      const ctaRoot = ctaGroup || buttonContainer || card;

      if (ctaGroup) {
        ctaGroup.classList.add('product-card-cta-group');
      }

      if (!ctaRoot) {
        return;
      }

      const addButton = getCardAddButton(card);
      const quantitySelector = ctaRoot.querySelector(QUANTITY_SELECTOR);
      const addButtonLabel = getCardAddButtonLabel(card);
      const matchingCartItemCandidates = findAllMatchingCartVariantItems(card);
      const matchingCartItem = matchingCartItemCandidates[0] || null;
      const selectorQuantity = getProductCardQuantity(quantitySelector);
      const cartQuantity = matchingCartItem ? getCartVariantQuantity(matchingCartItem) : null;
      const displayedQuantity =
        selectorQuantity ||
        cartQuantity ||
        getQuantityFromAddedLabel(addButtonLabel) ||
        1;
      const proxyStepper = syncProductCardProxyStepper(
        card,
        buttonContainer || ctaRoot,
        addButton,
        quantitySelector,
        matchingCartItemCandidates
      );
      const interactiveControls = [...ctaRoot.querySelectorAll('button, [role="button"]')]
        .filter((control) => !control.closest(CARD_PROXY_STEPPER_SELECTOR))
        .length;
      const hasQuantityControls =
        Boolean(quantitySelector) ||
        Boolean(proxyStepper) ||
        (!addButton && interactiveControls >= 2);
      const isAddedState =
        Boolean(matchingCartItem) ||
        hasQuantityControls ||
        isProductCardAddedLabel(addButtonLabel);
      const shouldUseProxyStepper = Boolean(proxyStepper);

      card.classList.toggle('product-card-is-added', isAddedState);
      syncProductCardPrimaryCtaSlot(
        ctaRoot,
        addButton,
        quantitySelector,
        proxyStepper,
        isAddedState,
        shouldUseProxyStepper
      );
      syncProductCardAddButtonLabel(addButton, isAddedState, hasQuantityControls, displayedQuantity);
      syncProductCardQuantitySelector(
        quantitySelector,
        isAddedState && !shouldUseProxyStepper,
        displayedQuantity
      );
      buttonContainer?.classList.toggle('product-card-quantity-controls', hasQuantityControls && isAddedState);
      syncProductCardAddedState(buttonContainer || ctaRoot, addButton, hasQuantityControls, isAddedState, displayedQuantity);
    });
  }

  function getDirectChildNode(container, element) {
    if (!container || !element || !container.contains(element)) {
      return null;
    }

    let node = element;

    while (node && node.parentElement && node.parentElement !== container) {
      node = node.parentElement;
    }

    return node?.parentElement === container ? node : null;
  }

  function syncProductCardPrimaryCtaSlot(
    ctaRoot,
    addButton,
    quantitySelector,
    proxyStepper,
    isAddedState,
    shouldUseProxyStepper
  ) {
    if (!ctaRoot) {
      return;
    }

    Array.from(ctaRoot.children).forEach((child) => {
      child.classList.remove('product-card-primary-cta-slot');
    });

    const primaryControl = isAddedState
      ? (shouldUseProxyStepper ? proxyStepper : quantitySelector) || addButton
      : addButton || proxyStepper || quantitySelector;

    if (!primaryControl || !ctaRoot.contains(primaryControl)) {
      return;
    }

    const primarySlot = getDirectChildNode(ctaRoot, primaryControl) || primaryControl;

    if (!primarySlot || !ctaRoot.contains(primarySlot)) {
      return;
    }

    primarySlot.classList.add('product-card-primary-cta-slot');
  }

  function isProductCardAddedLabel(label) {
    return /added|in\s+your\s+ritual/i.test(String(label || ''));
  }

  function syncProductCardAddButtonLabel(addButton, isAddedState, hasQuantityControls, quantity = 1) {
    if (!addButton) {
      return;
    }

    const label = isAddedState ? formatAddedCtaLabel(quantity) : PRODUCT_CARD_ADD_LABEL;
    const textTarget = addButton.querySelector('[data-text="true"]');
    const shouldHideAddButton = isAddedState && hasQuantityControls;

    if (textTarget) {
      setTextContentIfChanged(textTarget, label);
    } else if (addButton.tagName !== 'INPUT') {
      setTextContentIfChanged(addButton, label);
    }

    if ('value' in addButton) {
      if (addButton.value !== label) {
        addButton.value = label;
      }
      if (addButton.getAttribute('value') !== label) {
        addButton.setAttribute('value', label);
      }
    }

    if (addButton.getAttribute('aria-label') !== label) {
      addButton.setAttribute('aria-label', label);
    }
    addButton.classList.toggle('is-added', isAddedState && !shouldHideAddButton);
    addButton.classList.toggle('product-card-add-button-hidden', shouldHideAddButton);

    if (shouldHideAddButton) {
      addButton.setAttribute('aria-hidden', 'true');
      return;
    }

    addButton.removeAttribute('aria-hidden');
  }

  function syncProductCardQuantitySelector(quantitySelector, isAddedState, quantity = 1) {
    if (!quantitySelector) {
      return;
    }

    const stepperLabel =
      quantitySelector.querySelector('[data-subscription-quantity-label="true"]') ||
      document.createElement('span');

    stepperLabel.className = 'product-card-stepper-label';
    stepperLabel.dataset.subscriptionQuantityLabel = 'true';
    stepperLabel.dataset.subscriptionQuantityPart = 'label';
    setTextContentIfChanged(stepperLabel, formatAddedCtaLabel(quantity));

    Array.from(quantitySelector.children).forEach((child) => {
      if (child !== stepperLabel) {
        delete child.dataset.subscriptionQuantityPart;
        delete child.dataset.subscriptionQuantityHidden;
      }
    });

    quantitySelector.classList.toggle('product-card-quantity-selector-active', isAddedState);

    if (!isAddedState) {
      stepperLabel.remove();
      return;
    }

    const controlNodes = [...new Set(
      [...quantitySelector.querySelectorAll('button, [role="button"]')]
        .map((control) => getDirectChildNode(quantitySelector, control))
        .filter(Boolean)
    )];

    if (controlNodes.length < 2) {
      stepperLabel.remove();
      return;
    }

    const decreaseControl = controlNodes[0];
    const increaseControl = controlNodes[controlNodes.length - 1];

    decreaseControl.dataset.subscriptionQuantityPart = 'decrease';
    increaseControl.dataset.subscriptionQuantityPart = 'increase';

    if (!stepperLabel.isConnected) {
      quantitySelector.appendChild(stepperLabel);
    }

    Array.from(quantitySelector.children).forEach((child) => {
      if (child === stepperLabel || child === decreaseControl || child === increaseControl) {
        return;
      }

      child.dataset.subscriptionQuantityHidden = 'true';
    });
  }

  function syncProductCardAddedState(buttonContainer, addButton, hasQuantityControls, isAddedState, quantity = 1) {
    if (!buttonContainer) {
      return;
    }

    const existingState = buttonContainer.querySelector('[data-subscription-added-state="true"]');

    if (!isAddedState || addButton || hasQuantityControls) {
      existingState?.remove();
      return;
    }

    if (existingState) {
      setTextContentIfChanged(existingState, formatAddedCtaLabel(quantity));
      return;
    }

    const addedState = document.createElement('div');
    addedState.className = 'product-card-added-state';
    addedState.dataset.subscriptionAddedState = 'true';
    setTextContentIfChanged(addedState, formatAddedCtaLabel(quantity));
    buttonContainer.appendChild(addedState);
  }

  function getCardTitle(card) {
    return TITLE_SELECTORS.reduce(
      (found, selector) => found || card.querySelector(selector),
      null
    )?.textContent.trim() || '';
  }

  function normalizeLookupText(value) {
    return String(value || '')
      .toLowerCase()
      .replace(/&amp;/g, ' and ')
      .replace(/&/g, ' and ')
      .replace(/[^a-z0-9]+/g, ' ')
      .replace(/\bdefault title\b/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  function getSelectedCardVariantLabel(card) {
    const cardTitle = getCardTitle(card);
    const cardSelect = getCardVariantSelect(card);
    const selectedOption =
      [...(cardSelect?.options || [])].find((option) => option.value === String(cardSelect?.value || '')) ||
      cardSelect?.selectedOptions?.[0] ||
      null;

    return formatVariantOptionLabel(selectedOption?.textContent || '', cardTitle);
  }

  function getCartVariantQuantityControls(cartItem) {
    return cartItem?.querySelector('[data-skio-name="bab-cart-variant-quantity-controls"]') || null;
  }

  function getCartVariantQuantityControl(cartItem, direction) {
    const quantityControls = getCartVariantQuantityControls(cartItem);
    if (!quantityControls) {
      return null;
    }

    const explicitSelector =
      direction === 'decrease'
        ? '[data-skio-name="bab-cart-variant-quantity-decrease"]'
        : '[data-skio-name="bab-cart-variant-quantity-increase"]';
    const explicitControl = quantityControls.querySelector(explicitSelector);
    if (explicitControl) {
      return explicitControl;
    }

    const controls = [...quantityControls.querySelectorAll('button, [role="button"]')];
    if (!controls.length) {
      return null;
    }

    return direction === 'decrease' ? controls[0] : controls[controls.length - 1];
  }

  function getProductCardQuantitySelectorControl(quantitySelector, direction) {
    if (!quantitySelector) {
      return null;
    }

    const controls = [...new Set(
      [...quantitySelector.querySelectorAll('button, [role="button"]')]
        .map((control) => getDirectChildNode(quantitySelector, control))
        .filter(Boolean)
    )];

    if (controls.length < 2) {
      return null;
    }

    return direction === 'decrease' ? controls[0] : controls[controls.length - 1];
  }

  function getCartVariantRemoveControl(cartItem) {
    if (!cartItem) {
      return null;
    }

    return (
      cartItem.querySelector('[data-skio-name="bab-cart-variant-remove-button"] button') ||
      cartItem.querySelector('[data-skio-name="bab-cart-variant-remove-button"] [role="button"]') ||
      cartItem.querySelector('[data-skio-name="bab-cart-variant-remove-button"]')
    );
  }

  function getCartVariantIdCandidates(cartItem) {
    if (!cartItem) {
      return [];
    }

    return [
      cartItem.getAttribute('data-variant-id'),
      cartItem.getAttribute('data-product-variant-id'),
      cartItem.dataset.variantId,
      cartItem.dataset.productVariantId,
      ...[...cartItem.querySelectorAll('[data-variant-id], [data-product-variant-id], [data-value], input[value], button[value], option[value]')]
        .map((element) =>
          element.getAttribute('data-variant-id') ||
          element.getAttribute('data-product-variant-id') ||
          element.getAttribute('data-value') ||
          element.getAttribute('value')
        ),
    ]
      .map((value) => normalizeVariantId(value))
      .filter(Boolean);
  }

  function getCartVariantItemLookupText(cartItem) {
    if (!cartItem) {
      return '';
    }

    // Skio's mini-cart line items often identify the product only through an
    // <img alt="..."> (or an aria-label), with no visible text node for the
    // title — plain textContent misses that entirely, so pull those
    // attributes in too.
    const attributeTexts = [...cartItem.querySelectorAll('img[alt], [aria-label], [title]')].flatMap(
      (element) => [element.getAttribute('alt'), element.getAttribute('aria-label'), element.getAttribute('title')]
    );

    return [cartItem.textContent, ...attributeTexts].filter(Boolean).join(' ');
  }

  function rerouteQuantityDecreaseToRemove(event, cartItemCandidates, decreaseControl) {
    if (!decreaseControl || !decreaseControl.contains(event.target)) {
      return false;
    }

    const candidates = Array.isArray(cartItemCandidates)
      ? cartItemCandidates
      : cartItemCandidates
      ? [cartItemCandidates]
      : [];

    // Only let Skio's native decrease handler run when we can positively
    // confirm the line is above quantity 1. Skio's own handler crashes the
    // page (its error boundary swaps in "Failed to load page") whenever it's
    // asked to take a line to 0 *or* when it can't resolve the line at all —
    // which happens whenever our cart-item matching heuristic misses, or the
    // product card's UI is still showing "Added" after the cart was emptied
    // elsewhere and Skio hasn't re-rendered yet. Treat every case we're not
    // sure about the same as "quantity is 1": block Skio's handler entirely.
    if (candidates.some((cartItem) => getCartVariantQuantity(cartItem) > 1)) {
      return false;
    }

    const removeControl = candidates.map(getCartVariantRemoveControl).find(Boolean);

    event.preventDefault();
    event.stopImmediatePropagation();

    if (removeControl) {
      removeControl.click();
    } else {
      // No cart item resolvable yet — verified live against production that
      // this is not a rare edge case: clicking Skio's native decrease
      // button in this exact window (right after Add, before our own sync
      // has run even once) triggers a full document reload inside Skio's
      // app instead of an in-place update, which is what actually crashes
      // under real network/script load. A button that's a no-op for one
      // click while state catches up is a far better outcome than that.
      scheduleSkioEnhancements();
    }

    return true;
  }

  function getCartVariantItemMatchScore(cartItem, card) {
    const cardTitle = getCardTitle(card);
    const normalizedTitle = normalizeLookupText(cardTitle);

    if (!normalizedTitle) {
      return 0;
    }

    const normalizedVariantLabel = normalizeLookupText(getSelectedCardVariantLabel(card));
    const normalizedHandle = normalizeLookupText(getProductHandle(card, cardTitle).replace(/-/g, ' '));
    const normalizedVariantId = normalizeVariantId(getCardVariantSelect(card)?.value);
    const cartVariantIds = getCartVariantIdCandidates(cartItem);
    const explicitCartHandle = getCartVariantProductHandle(cartItem) || '';
    const normalizedExplicitCartHandle = normalizeLookupText(explicitCartHandle.replace(/-/g, ' '));
    const itemText = normalizeLookupText(getCartVariantItemLookupText(cartItem));
    const itemHtml = cartItem.innerHTML || '';
    const productLink = cartItem.querySelector('a[href*="/products/"]');
    const linkedHandle = productLink?.href.match(/\/products\/([^/?#]+)/)?.[1] || '';
    const normalizedLinkedHandle = normalizeLookupText(linkedHandle.replace(/-/g, ' '));

    let score = 0;

    if (
      normalizedVariantId &&
      (cartVariantIds.includes(normalizedVariantId) || itemHtml.includes(normalizedVariantId))
    ) {
      score += 15;
    }

    if (normalizedExplicitCartHandle && normalizedExplicitCartHandle === normalizedHandle) {
      score += 10;
    } else if (normalizedLinkedHandle && normalizedLinkedHandle === normalizedHandle) {
      score += 8;
    } else if (normalizedHandle && itemText.includes(normalizedHandle)) {
      score += 5;
    }

    if (normalizedVariantId && itemHtml.includes(normalizedVariantId)) {
      score += 10;
    }

    if (itemText.includes(normalizedTitle)) {
      score += 4;
    }

    if (normalizedVariantLabel && itemText.includes(normalizedVariantLabel)) {
      score += 3;
    }

    // A tiebreaker only — having quantity controls says nothing about which
    // product this cart item is, so it must never be the sole reason a card
    // is considered a match (that would match every card to any lone cart
    // item regardless of product identity).
    if (score > 0 && getCartVariantQuantityControls(cartItem)) {
      score += 1;
    }

    return score;
  }

  function findMatchingCartVariantItem(card) {
    return findAllMatchingCartVariantItems(card)[0] || null;
  }

  // Mobile and desktop render separate DOM trees for the cart (the mobile
  // summary bar can be a stripped-down preview with no inline +/- controls
  // even though a fuller cart element for the same line item exists
  // elsewhere). Returning every scope's match — not just the first — lets
  // callers that need a specific control fall back to whichever matched
  // element actually has it, instead of silently no-op'ing against a
  // control-less preview node.
  function findAllMatchingCartVariantItems(card) {
    const scopes = getCartVariantItemScopes();
    const visibleScopes = scopes.filter((scope) => scope !== document && isElementVisible(scope));
    const orderedScopes = [];

    [...visibleScopes, ...scopes].forEach((scope) => {
      if (!orderedScopes.includes(scope)) {
        orderedScopes.push(scope);
      }
    });

    const matches = [];
    orderedScopes.forEach((scope) => {
      const { bestMatch, bestScore } = findBestMatchingCartVariantItemInScope(scope, card);
      if (bestMatch && bestScore > 0 && !matches.includes(bestMatch)) {
        matches.push(bestMatch);
      }
    });

    return matches;
  }

  function findBestMatchingCartVariantItemInScope(scope, card) {
    if (!scope?.querySelectorAll) {
      return { bestMatch: null, bestScore: 0 };
    }

    const cartItems = [...scope.querySelectorAll('[data-skio-name="bab-cart-variant-item"]')];
    if (!cartItems.length) {
      return { bestMatch: null, bestScore: 0 };
    }

    const cardTitle = getCardTitle(card);
    const normalizedCardHandle = normalizeLookupText(
      getProductHandle(card, cardTitle).replace(/-/g, ' ')
    );
    const normalizedVariantLabel = normalizeLookupText(getSelectedCardVariantLabel(card));
    const selectedVariantId = normalizeVariantId(getCardVariantSelect(card)?.value);

    if (selectedVariantId) {
      const exactVariantMatch = cartItems.find((cartItem) => {
        const variantIds = getCartVariantIdCandidates(cartItem);
        return (
          variantIds.includes(selectedVariantId) ||
          (cartItem.innerHTML || '').includes(selectedVariantId)
        );
      });

      if (exactVariantMatch) {
        return { bestMatch: exactVariantMatch, bestScore: 100 };
      }
    }

    const exactHandleMatches = cartItems.filter((cartItem) => {
      const cartHandle = getCartVariantProductHandle(cartItem);
      return (
        cartHandle &&
        normalizeLookupText(cartHandle.replace(/-/g, ' ')) === normalizedCardHandle
      );
    });

    if (exactHandleMatches.length === 1) {
      return { bestMatch: exactHandleMatches[0], bestScore: 90 };
    }

    if (exactHandleMatches.length > 1 && normalizedVariantLabel) {
      const exactHandleAndVariantMatch = exactHandleMatches.find((cartItem) =>
        normalizeLookupText(getCartVariantItemLookupText(cartItem)).includes(normalizedVariantLabel)
      );
      if (exactHandleAndVariantMatch) {
        return { bestMatch: exactHandleAndVariantMatch, bestScore: 95 };
      }
    }

    let bestMatch = null;
    let bestScore = 0;

    cartItems.forEach((cartItem) => {
      const score = getCartVariantItemMatchScore(cartItem, card);

      if (score > bestScore) {
        bestScore = score;
        bestMatch = cartItem;
      }
    });

    if (bestMatch && bestScore > 0) {
      return { bestMatch, bestScore };
    }

    if (exactHandleMatches.length > 0) {
      return { bestMatch: exactHandleMatches[0], bestScore: 1 };
    }

    return { bestMatch, bestScore };
  }

  function buildProductCardProxyStepper(card) {
    const stepper = document.createElement('div');
    stepper.className = 'product-card-proxy-stepper';
    stepper.dataset.subscriptionCardStepper = 'true';
    stepper.innerHTML = `
      <button type="button" data-subscription-stepper-action="decrease" aria-label="Decrease quantity">-</button>
      <span class="product-card-stepper-label" data-subscription-stepper-label="true">${ADDED_CTA_LABEL}</span>
      <button type="button" data-subscription-stepper-action="increase" aria-label="Increase quantity">+</button>
    `;

    stepper.addEventListener('click', (event) => {
      const actionButton = event.target.closest('[data-subscription-stepper-action]');
      if (!actionButton) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();

      const action = actionButton.dataset.subscriptionStepperAction;
      const cartItemCandidates = findAllMatchingCartVariantItems(card);

      if (action === 'decrease') {
        // At quantity 1, Skio's own decrease button would drop the line
        // item to 0, a state its cart app can't handle gracefully (it
        // crashes into an error screen). Use the remove control instead so
        // the item is dropped from the cart properly.
        const decreaseControl = cartItemCandidates
          .map((cartItem) => (getCartVariantQuantity(cartItem) > 1 ? getCartVariantQuantityControl(cartItem, 'decrease') : null))
          .find(Boolean);
        const removeControl = cartItemCandidates.map(getCartVariantRemoveControl).find(Boolean);
        (decreaseControl || removeControl)?.click();
      } else if (action === 'increase') {
        const increaseControl =
          cartItemCandidates.map((cartItem) => getCartVariantQuantityControl(cartItem, 'increase')).find(Boolean) ||
          getCardAddButton(card);
        increaseControl?.click();
      }

      scheduleSkioEnhancements();
      window.setTimeout(scheduleSkioEnhancements, 150);
    });

    return stepper;
  }

  function syncProductCardProxyStepper(card, container, addButton, nativeQuantitySelector, matchedCartItemCandidates) {
    const existingStepper = card.querySelector(CARD_PROXY_STEPPER_SELECTOR);

    if (!container) {
      existingStepper?.remove();
      return null;
    }

    const cartItemCandidates = matchedCartItemCandidates || findAllMatchingCartVariantItems(card);
    const cartItem = cartItemCandidates[0];
    if (!cartItem) {
      existingStepper?.remove();
      return null;
    }

    const stepper = existingStepper || buildProductCardProxyStepper(card);
    const decreaseButton = stepper.querySelector('[data-subscription-stepper-action="decrease"]');
    const increaseButton = stepper.querySelector('[data-subscription-stepper-action="increase"]');
    const label = stepper.querySelector('[data-subscription-stepper-label="true"]');
    const hasDecreaseControl = cartItemCandidates.some(
      (candidate) => getCartVariantQuantityControl(candidate, 'decrease') || getCartVariantRemoveControl(candidate)
    );
    const hasIncreaseControl =
      cartItemCandidates.some((candidate) => getCartVariantQuantityControl(candidate, 'increase')) ||
      Boolean(addButton);

    setTextContentIfChanged(label, formatAddedCtaLabel(getCartVariantQuantity(cartItem)));
    decreaseButton.disabled = !hasDecreaseControl;
    increaseButton.disabled = !hasIncreaseControl;

    if (!stepper.isConnected) {
      const nativeQuantityNode = getDirectChildNode(container, nativeQuantitySelector);
      const addButtonNode =
        addButton && addButton.parentElement === container ? addButton : null;
      const insertBeforeNode = nativeQuantityNode || addButtonNode;

      if (insertBeforeNode) {
        container.insertBefore(stepper, insertBeforeNode);
      } else {
        container.appendChild(stepper);
      }
    }

    stepper.classList.add('product-card-proxy-stepper-active');
    stepper.hidden = false;
    return stepper;
  }

  function setupSkioEnhancements() {
    if (isSkioReady()) {
      runSkioEnhancements();
      observeSkioRoot();
      return;
    }

    if (skioReadyObserver) {
      return;
    }

    skioReadyObserver = new MutationObserver(() => {
      if (!isSkioReady()) {
        return;
      }

      skioReadyObserver.disconnect();
      skioReadyObserver = null;
      runSkioEnhancements();
      observeSkioRoot();
    });

    skioReadyObserver.observe(document.documentElement, {
      childList: true,
      subtree: true,
    });
  }

  function observeSkioRoot() {
    if (skioMutationObserver) {
      return;
    }

    const skioRoot = getSkioRoot();
    if (!skioRoot) {
      return;
    }

    skioMutationObserver = new MutationObserver(() => {
      scheduleSkioEnhancements();
    });

    skioMutationObserver.observe(skioRoot, {
      childList: true,
      subtree: true,
    });
  }

  let skioEnhancementsTimer = null;

  function scheduleSkioEnhancements() {
    clearTimeout(skioEnhancementsTimer);
    skioEnhancementsTimer = setTimeout(() => {
      skioEnhancementsTimer = null;
      runSkioEnhancements();
    }, 500);
  }

  function runSkioEnhancements() {
    if (!isSkioReady()) {
      return;
    }

    // Run each enhancement step in isolation so one failing step (e.g. a
    // selector miss against Skio's markup on a given render) can't block
    // every step after it, such as the price sync or CTA layout sync.
    const runStep = (fn) => {
      try {
        fn();
      } catch (error) {
        // Swallow — a failed step should not block the remaining steps.
      }
    };

    runStep(renderThemeCartProgress);

    runStep(markUpsellGrid);
    runStep(attachDetailButtons);
    runStep(injectCardVariantSelectors);
    runStep(syncProductCardCtaLayout);
    runStep(swapCardImages);
    runStep(addProductDescription);
    runStep(applyProductFilter);
  }

  function renderThemeCartProgress() {
    const cart = document.querySelector(SKIO_CART_SELECTOR);
    if (!cart || !window.matchMedia('(min-width: 769px)').matches) return;
    let progress = document.querySelector('[data-theme-cart-progress]');
    if (!progress) {
      progress = document.createElement('div');
      progress.dataset.themeCartProgress = 'true';
      progress.setAttribute('aria-hidden', 'true');
      progress.innerHTML = '<span><b>1</b><i>1 product</i></span><span><b>2</b><i>2 products</i></span><span><b>3</b><i>3+ products</i></span>';
      document.body.append(progress);
    }
    const rect = cart.getBoundingClientRect();
    progress.style.left = `${rect.left + window.scrollX + 42}px`;
    progress.style.top = `${rect.top + window.scrollY + 82}px`;
    progress.style.width = `${rect.width - 84}px`;

    // Keep the promotional badge outside Skio's React-owned cart while
    // positioning it beside the native frequency group. Adding this node to
    // the cart itself can break Skio's third-item reconciliation.
    let saveBadge = document.querySelector('[data-theme-cart-save-badge]');
    if (!saveBadge) {
      saveBadge = document.createElement('div');
      saveBadge.dataset.themeCartSaveBadge = 'true';
      saveBadge.setAttribute('aria-hidden', 'true');
      document.body.append(saveBadge);
    }

    const discountMatch = getSubscriptionDiscountText().match(/\d+(?:\.\d+)?%/);
    saveBadge.textContent = `SAVE ${discountMatch?.[0] || '25%'}`;

    const frequencyGroup = cart.querySelector('[data-skio-name="bab-cart-frequency-group"]');
    if (frequencyGroup) {
      const frequencyRect = frequencyGroup.getBoundingClientRect();
      saveBadge.style.display = 'block';
      saveBadge.style.left = `${rect.right + window.scrollX - 126}px`;
      saveBadge.style.top = `${frequencyRect.top + window.scrollY + 1}px`;
    } else {
      saveBadge.style.display = 'none';
    }
  }

  let _cartStickyScrollOff = null;
  let _cartStickyResizeOff = null;
  let _cartStickyRaf = null;

  function getPixelValue(value) {
    const parsedValue = parseFloat(value);
    return Number.isFinite(parsedValue) ? parsedValue : 0;
  }

  function getActiveStickyHeaderHeight() {
    const stickyHeader = document.getElementById('sticky-header');
    if (!stickyHeader) {
      return 0;
    }

    return window.getComputedStyle(stickyHeader).position === 'fixed'
      ? stickyHeader.getBoundingClientRect().height
      : 0;
  }

  function applyCartSticky() {
    // Teardown any previous listeners
    if (_cartStickyScrollOff) { _cartStickyScrollOff(); _cartStickyScrollOff = null; }
    if (_cartStickyResizeOff) { _cartStickyResizeOff(); _cartStickyResizeOff = null; }

    const cart = document.querySelector(SKIO_CART_SELECTOR);
    const footer =
      document.querySelector('footer.footer') ||
      document.querySelector('#shopify-section-footer footer') ||
      document.querySelector('[id^="shopify-section-footer"] footer');
    if (cart) {
      cart.style.removeProperty('transform');
      cart.style.removeProperty('max-height');
    }

    if (window.innerWidth < 1024 || !cart) return;


    // Measure the cart's natural document-relative top after resetting transform
    const rect = cart.getBoundingClientRect();
    const cartDocTop = rect.top + window.scrollY;
    const cartTopGap = Math.max(0, getPixelValue(window.getComputedStyle(cart).top));

    function tick() {
      const stickyTop = getActiveStickyHeaderHeight() + cartTopGap;
      const maxHeight = Math.max(0, window.innerHeight - stickyTop - cartTopGap);

      cart.style.setProperty('max-height', maxHeight + 'px', 'important');

      let dy = Math.max(0, window.scrollY + stickyTop - cartDocTop);

      if (footer) {
        const footerTop = footer.getBoundingClientRect().top + window.scrollY;
        const cartHeight = cart.getBoundingClientRect().height;
        const maxDy = Math.max(0, footerTop - cartHeight - cartDocTop);
        dy = Math.min(dy, maxDy);
      }

      cart.style.setProperty('transform', 'translateY(' + dy + 'px)', 'important');
    }

    function onScroll() {
      if (_cartStickyRaf) return;
      _cartStickyRaf = requestAnimationFrame(function () {
        _cartStickyRaf = null;
        tick();
      });
    }

    function onResize() {
      if (_cartStickyScrollOff) { _cartStickyScrollOff(); _cartStickyScrollOff = null; }
      if (_cartStickyResizeOff) { _cartStickyResizeOff(); _cartStickyResizeOff = null; }
      if (cart) cart.style.removeProperty('transform');
      applyCartSticky();
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onResize, { once: true });
    _cartStickyScrollOff = function () { window.removeEventListener('scroll', onScroll); };
    _cartStickyResizeOff = function () { window.removeEventListener('resize', onResize); };

    tick();
  }

  function getSkioRoot() {
    return document.querySelector(SKIO_ROOT_SELECTOR);
  }

  function isSkioReady() {
    const skioRoot = getSkioRoot();
    if (!skioRoot) {
      return false;
    }

    return Boolean(
      skioRoot.querySelector(SKIO_PRODUCT_GRID_SELECTOR) &&
      (
        skioRoot.querySelector(SKIO_CART_SELECTOR) ||
        skioRoot.querySelector(SKIO_MOBILE_CART_SELECTOR)
      )
    );
  }

  function markUpsellGrid() {
    document.querySelectorAll(`.${UPSELL_GRID_CLASS}`).forEach((element) => {
      element.classList.remove(UPSELL_GRID_CLASS);
    });

    const explicitGrid = document.querySelector(SKIO_PRODUCT_GRID_SELECTOR);
    if (explicitGrid) {
      explicitGrid.classList.add(UPSELL_GRID_CLASS);
      return;
    }

    const root = getSkioRoot();
    const cards = [...document.querySelectorAll(SKIO_CARD_SELECTOR)];

    if (!root || cards.length < 2) {
      return;
    }

    let candidate = cards[0].parentElement;

    while (candidate && candidate !== root) {
      if (
        cards.every((card) => candidate.contains(card)) &&
        !candidate.querySelector(SKIO_CART_SELECTOR) &&
        !candidate.querySelector(SKIO_MOBILE_CART_SELECTOR)
      ) {
        candidate.classList.add(UPSELL_GRID_CLASS);
        return;
      }

      candidate = candidate.parentElement;
    }
  }

  function syncStepHeadings() {
    const isStep2 = isSubscriptionSecondStepActive();
    document.documentElement.classList.toggle('subscription-step-2', isStep2);
    if (isStep2) {
      placeHeadingsInGrid();
    }
  }

  function placeHeadingsInGrid() {
    if (window.matchMedia('(max-width: 768px)').matches) {
      return;
    }

    const upsellGrid = document.querySelector('[data-skio-name="dynamic-box-upsell-grid"]');
    if (!upsellGrid) return;

    const gridContainer = upsellGrid.parentElement;
    if (!gridContainer) return;

    if (gridContainer.querySelector(':scope > .subscription-upsell-header-block')) return;

    const block = document.createElement('div');
    block.className = 'subscription-upsell-header-block';

    const h1 = document.querySelector('.flex.flex-col.w-full > h1');
    const h2 = document.querySelector('.flex.flex-col.w-full > h2');
    if (h1) {
      const h1Copy = document.createElement('h1');
      h1Copy.innerHTML = h1.innerHTML;
      block.appendChild(h1Copy);
    }
    if (h2) {
      const h2Copy = document.createElement('h2');
      h2Copy.innerHTML = h2.innerHTML;
      block.appendChild(h2Copy);
    }

    upsellGrid.insertAdjacentElement('beforebegin', block);
  }

  function parseDiscountSteps(value) {
    return String(value == null ? '' : value)
      .replace(/<[^>]*>/g, '')
      .split(',')
      .map((entry) => entry.trim())
      .filter(Boolean);
  }

  function getConfiguredDiscountSteps(mode = 'subscription') {
    if (mode === 'onetime') {
      if (!isOneTimeVolumeDiscountEnabled()) {
        return [];
      }

      const configuredSteps = parseDiscountSteps(
        typeof oneTimeDiscountStep === 'undefined' ? '<p>0%,5%,10%</p>' : oneTimeDiscountStep
      );

      return configuredSteps.length ? configuredSteps : ['0%', '5%', '10%'];
    }

    return parseDiscountSteps(typeof discountStep === 'undefined' ? '' : discountStep);
  }

  function isOneTimeVolumeDiscountEnabled() {
    return typeof oneTimeVolumeDiscountEnabled === 'undefined'
      ? true
      : oneTimeVolumeDiscountEnabled !== false;
  }

  function getDiscountPercentFromText(text) {
    const match = String(text || '').match(/(\d+(?:\.\d+)?)/);
    const percent = match ? Number(match[1]) : 0;
    return Number.isFinite(percent) ? percent : 0;
  }

  function getDiscountTextForMode(mode) {
    const steps = getConfiguredDiscountSteps(mode);

    if (!steps.length) {
      return '';
    }

    const itemCount = getBoxItemCount();
    const index = Math.min(Math.max(itemCount - 1, 0), steps.length - 1);
    const discountText = steps[index] || '';
    return getDiscountPercentFromText(discountText) > 0 ? discountText : '';
  }

  function getSubscriptionDiscountPercent() {
    return getDiscountPercentFromText(getSubscriptionDiscountText());
  }

  function getOneTimeDiscountPercent() {
    return getDiscountPercentFromText(getOneTimeDiscountText());
  }

  function formatSubscriptionMoney(cents) {
    if (typeof Shopify !== 'undefined' && typeof Shopify.formatMoney === 'function') {
      return Shopify.formatMoney(cents, window.cartStrings?.money_format || window.money_format);
    }

    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format((Number(cents) || 0) / 100);
  }

  function parseSubscriptionMoney(text) {
    const rawText = String(text || '').trim();
    if (!rawText) {
      return null;
    }

    const isNegative = /\(/.test(rawText) || /-\s*[$\d]/.test(rawText);
    const numericSegments = rawText.match(/\d[\d,.]*/g);
    const cleaned = String(numericSegments?.[numericSegments.length - 1] || '').replace(/[^\d.,]/g, '');
    if (!cleaned) {
      return null;
    }

    const lastDot = cleaned.lastIndexOf('.');
    const lastComma = cleaned.lastIndexOf(',');
    const lastSeparatorIndex = Math.max(lastDot, lastComma);

    let normalizedAmount = cleaned.replace(/[^\d]/g, '');

    if (lastSeparatorIndex >= 0) {
      const fraction = cleaned.slice(lastSeparatorIndex + 1).replace(/[^\d]/g, '');
      if (fraction.length > 0 && fraction.length <= 2) {
        const whole = cleaned.slice(0, lastSeparatorIndex).replace(/[^\d]/g, '') || '0';
        normalizedAmount = `${whole}.${fraction.padEnd(2, '0')}`;
      }
    }

    const amount = Number.parseFloat(normalizedAmount);
    if (!Number.isFinite(amount)) {
      return null;
    }

    const cents = Math.round(amount * 100);
    return isNegative ? -cents : cents;
  }

  function renderOneTimeFrequencyPrice(priceNode, cents) {
    if (!priceNode) {
      return;
    }

    const renderState = `base:${cents}`;
    if (priceNode.dataset.subscriptionPriceState === renderState) {
      return;
    }

    priceNode.dataset.subscriptionPriceState = renderState;
    priceNode.dataset.subscriptionBasePriceCents = String(cents);
    delete priceNode.dataset.subscriptionCurrentPriceCents;
    priceNode.style.display = '';
    priceNode.style.alignItems = '';
    priceNode.style.gap = '';
    priceNode.style.whiteSpace = '';
    priceNode.removeAttribute('aria-label');
    priceNode.textContent = formatSubscriptionMoney(cents);
  }

  function renderDiscountedFrequencyPrice(priceNode, compareCents, currentCents, label) {
    if (!priceNode) {
      return;
    }

    const renderState = `${label}:${compareCents}:${currentCents}`;
    if (priceNode.dataset.subscriptionPriceState === renderState && priceNode.children.length >= 2) {
      return;
    }

    const compare = document.createElement('span');
    compare.innerHTML = `<span class="visually-hidden">Regular price</span>${formatSubscriptionMoney(compareCents)}`;
    compare.style.opacity = '0.58';
    compare.style.textDecoration = 'line-through';

    const current = document.createElement('span');
    current.innerHTML = `<span class="visually-hidden">Subscription price</span>${formatSubscriptionMoney(currentCents)}`;
    current.style.fontWeight = '600';

    priceNode.dataset.subscriptionPriceState = renderState;
    priceNode.dataset.subscriptionBasePriceCents = String(compareCents);
    priceNode.dataset.subscriptionCurrentPriceCents = String(currentCents);
    priceNode.style.display = 'inline-flex';
    priceNode.style.alignItems = 'baseline';
    priceNode.style.gap = '8px';
    priceNode.style.whiteSpace = 'nowrap';
    priceNode.setAttribute(
      'aria-label',
      `${label} ${formatSubscriptionMoney(currentCents)}; regular price ${formatSubscriptionMoney(compareCents)}`
    );
    priceNode.replaceChildren(compare, current);
  }

  function renderSubscriptionFrequencyPrice(priceNode, compareCents, currentCents) {
    renderDiscountedFrequencyPrice(priceNode, compareCents, currentCents, 'Subscription price');
  }

  function renderOneTimeDiscountedFrequencyPrice(priceNode, compareCents, currentCents) {
    renderDiscountedFrequencyPrice(priceNode, compareCents, currentCents, 'One-time volume price');
  }

  function syncSubscriptionComparePricing() {
    if (!isSubscriptionModeSelected()) {
      return;
    }

    const discountPercent = getSubscriptionDiscountPercent();
    if (!discountPercent || discountPercent <= 0 || discountPercent >= 100) {
      return;
    }

    getFrequencyPriceScopes().forEach((scope) => {
      const subscriptionPrice = scope.querySelector(
        '[data-skio-name="bab-cart-frequency-price-subscription"]'
      );
      if (!subscriptionPrice) {
        return;
      }

      const basePriceCents = resolveBundleBasePriceFromAddedCardsSync();
      if (Number.isFinite(basePriceCents) && basePriceCents > 0) {
        const currentCents = Math.round(basePriceCents * ((100 - discountPercent) / 100));
        if (Number.isFinite(currentCents) && currentCents > 0 && currentCents < basePriceCents) {
          renderSubscriptionFrequencyPrice(subscriptionPrice, basePriceCents, currentCents);
          return;
        }
      }

      const storedCurrentPrice = Number(subscriptionPrice.dataset.subscriptionCurrentPriceCents);
      const currentCents =
        Number.isFinite(storedCurrentPrice) && storedCurrentPrice > 0
          ? storedCurrentPrice
          : parseSubscriptionMoney(subscriptionPrice.textContent);
      if (!Number.isFinite(currentCents) || currentCents <= 0) {
        return;
      }

      const compareCents = Math.round(currentCents / ((100 - discountPercent) / 100));
      if (!Number.isFinite(compareCents) || compareCents <= currentCents) {
        return;
      }

      renderSubscriptionFrequencyPrice(subscriptionPrice, compareCents, currentCents);
    });
  }

  function getFrequencyPriceScopes() {
    const scopes = getPurchaseModeScopes().filter((scope) => scope !== document);
    return scopes.length ? scopes : [document];
  }

  function parsePositiveInteger(value) {
    if (value == null) {
      return null;
    }

    const match = String(value).trim().match(/^\d+$/);
    if (!match) {
      return null;
    }

    const parsed = Number.parseInt(match[0], 10);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  }

  function getQuantityFromAddedLabel(label) {
    const match = String(label || '').match(/\b(\d+)\b/);
    return parsePositiveInteger(match?.[1] || null);
  }

  function getProductCardQuantity(quantitySelector) {
    if (!quantitySelector) {
      return null;
    }

    const explicitQuantity = parsePositiveInteger(
      quantitySelector.querySelector('[data-skio-name="quantity-selector-quantity"]')?.textContent
    );
    if (explicitQuantity) {
      return explicitQuantity;
    }

    const candidates = [
      ...quantitySelector.querySelectorAll(
        'input[type="number"], input[name*="quantity" i], [role="spinbutton"], [aria-valuenow], [data-quantity], [data-qty]'
      ),
      ...quantitySelector.children,
    ];

    for (const element of candidates) {
      if ('value' in element) {
        const parsed = parsePositiveInteger(element.value);
        if (parsed) {
          return parsed;
        }
      }
    }

    for (const element of candidates) {
      const parsed = parsePositiveInteger(element.textContent);
      if (parsed) {
        return parsed;
      }
    }

    const attrNames = ['aria-valuenow', 'data-quantity', 'data-qty', 'value'];
    for (const element of candidates) {
      for (const attrName of attrNames) {
        const parsed = parsePositiveInteger(element.getAttribute?.(attrName));
        if (parsed) {
          return parsed;
        }
      }
    }

    return null;
  }

  function getAddedProductCards() {
    return [...document.querySelectorAll(SKIO_CARD_SELECTOR)].filter((card) => {
      const addButtonLabel = getCardAddButtonLabel(card);
      return (
        card.classList.contains('product-card-is-added') ||
        isProductCardAddedLabel(addButtonLabel) ||
        getProductCardQuantity(card.querySelector(QUANTITY_SELECTOR))
      );
    });
  }

  function getProductCardPriceCents(card) {
    const priceNode = card?.querySelector('[data-skio-name="product-card-price"]');
    const priceCents = parseSubscriptionMoney(priceNode?.textContent);
    return Number.isFinite(priceCents) && priceCents >= 0 ? priceCents : null;
  }

  function resolveBundleBasePriceFromAddedCardsSync() {
    const addedCards = getAddedProductCards();
    if (!addedCards.length) {
      return null;
    }

    const lineTotals = addedCards.map((card) => {
      const quantity =
        getProductCardQuantity(card.querySelector(QUANTITY_SELECTOR)) ||
        getQuantityFromAddedLabel(getCardAddButtonLabel(card)) ||
        1;
      const priceCents = getProductCardPriceCents(card);
      return Number.isFinite(priceCents) ? priceCents * quantity : null;
    });

    if (lineTotals.some((lineTotal) => !Number.isFinite(lineTotal))) {
      return null;
    }

    const totalCents = lineTotals.reduce((sum, lineTotal) => sum + lineTotal, 0);
    return totalCents > 0 ? totalCents : null;
  }

  function getCartVariantProductHandle(cartItem) {
    if (!cartItem) {
      return null;
    }

    const explicitHandle =
      cartItem.getAttribute('data-product-handle') ||
      cartItem.dataset.productHandle ||
      cartItem.querySelector('[data-product-handle]')?.dataset.productHandle;

    if (explicitHandle) {
      return explicitHandle;
    }

    const productLink = cartItem.querySelector('a[href*="/products/"]');
    const match = productLink?.href.match(/\/products\/([^/?#]+)/);
    return match?.[1] || null;
  }

  function getCartVariantQuantity(cartItem) {
    if (!cartItem) {
      return 1;
    }

    // Skio renders the live count in a dedicated element — read it directly
    // rather than scanning for any numeric-looking text, which can match the
    // wrong node once other numbers (tier badges, discount text) are nearby.
    const explicitQuantity = parsePositiveInteger(
      cartItem.querySelector('[data-skio-name="bab-cart-variant-quantity"]')?.textContent
    );
    if (explicitQuantity) {
      return explicitQuantity;
    }

    const quantityControls = getCartVariantQuantityControls(cartItem);
    const scopedCandidates = quantityControls
      ? [
          ...quantityControls.querySelectorAll(
            'input[type="number"], input[name*="quantity" i], [role="spinbutton"], [aria-valuenow], [data-quantity], [data-qty]'
          ),
          ...quantityControls.children,
        ]
      : [];
    const fallbackCandidates = [
      ...cartItem.querySelectorAll(
        'input[type="number"], input[name*="quantity" i], [role="spinbutton"], [aria-valuenow], [data-quantity], [data-qty]'
      ),
    ];

    // Controlled inputs (React/Vue) update the live `.value` property without
    // touching the `value` attribute, so getAttribute('value') would keep
    // returning the stale initial render value forever. Read the live
    // property first before falling back to attributes.
    for (const element of [...scopedCandidates, ...fallbackCandidates]) {
      if ('value' in element) {
        const parsed = parsePositiveInteger(element.value);
        if (parsed) {
          return parsed;
        }
      }
    }

    // Text content always reflects what's actually rendered, so it's a more
    // reliable live signal than attributes that may only be set once at
    // initial mount.
    if (quantityControls) {
      const exactCountElement = [...quantityControls.querySelectorAll('*')].find((element) =>
        Boolean(parsePositiveInteger(element.textContent))
      );
      const parsed = parsePositiveInteger(exactCountElement?.textContent);
      if (parsed) {
        return parsed;
      }
    }

    const attrNames = ['aria-valuenow', 'data-quantity', 'data-qty', 'value'];
    for (const element of [...scopedCandidates, ...fallbackCandidates]) {
      for (const attrName of attrNames) {
        const parsed = parsePositiveInteger(element.getAttribute?.(attrName));
        if (parsed) {
          return parsed;
        }
      }
    }

    return 1;
  }

  function syncCartVariantQuantityBadges() {
    document.querySelectorAll('[data-skio-name="bab-cart-variant-item"]').forEach((cartItem) => {
      const quantity = getCartVariantQuantity(cartItem);
      let badge = cartItem.querySelector('[data-subscription-quantity-badge="true"]');

      if (!badge) {
        badge = document.createElement('span');
        badge.className = 'subscription-cart-variant-quantity-badge';
        badge.dataset.subscriptionQuantityBadge = 'true';
        cartItem.prepend(badge);
      }

      setTextContentIfChanged(badge, String(quantity));
      badge.hidden = false;
    });
  }

  function buildCartItemToCardMap() {
    const cartItemMap = new Map();

    document.querySelectorAll(SKIO_CARD_SELECTOR).forEach((card) => {
      const cartItem = findMatchingCartVariantItem(card);
      if (cartItem && !cartItemMap.has(cartItem)) {
        cartItemMap.set(cartItem, card);
      }
    });

    return cartItemMap;
  }

  function findMatchingProductVariant(product, cartItem, matchedCard) {
    const variants = product?.variants || [];
    if (!variants.length) {
      return null;
    }

    const selectedVariantId = normalizeVariantId(matchedCard ? getCardVariantSelect(matchedCard)?.value : '');
    if (selectedVariantId) {
      const selectedVariant = variants.find(
        (variant) => normalizeVariantId(variant.id) === selectedVariantId
      );
      if (selectedVariant) {
        return selectedVariant;
      }
    }

    const variantIds = new Set(variants.map((variant) => normalizeVariantId(variant.id)));
    const variantIdCandidates = [
      cartItem.getAttribute('data-variant-id'),
      cartItem.getAttribute('data-product-variant-id'),
      cartItem.dataset.variantId,
      cartItem.dataset.productVariantId,
      ...[...cartItem.querySelectorAll('[data-variant-id], [data-product-variant-id], [data-value], input[value], button[value], option[value]')]
        .map((element) =>
          element.getAttribute('data-variant-id') ||
          element.getAttribute('data-product-variant-id') ||
          element.getAttribute('data-value') ||
          element.getAttribute('value')
        ),
    ]
      .map((value) => normalizeVariantId(value))
      .filter(Boolean);

    const explicitVariantId = variantIdCandidates.find((value) => variantIds.has(value));
    if (explicitVariantId) {
      return variants.find((variant) => normalizeVariantId(variant.id) === explicitVariantId) || null;
    }

    const itemHtml = cartItem.innerHTML || '';
    const htmlVariantMatch = variants.find((variant) => itemHtml.includes(String(variant.id)));
    if (htmlVariantMatch) {
      return htmlVariantMatch;
    }

    const itemText = normalizeLookupText(getCartVariantItemLookupText(cartItem));
    if (itemText) {
      let bestMatch = null;
      let bestScore = 0;

      variants.forEach((variant) => {
        const normalizedTitle = normalizeLookupText(variant.title);
        const normalizedLabel = normalizeLookupText(
          formatVariantOptionLabel(variant.title, product.title)
        );
        let score = 0;

        if (normalizedLabel && itemText.includes(normalizedLabel)) {
          score += 4;
        }

        if (normalizedTitle && itemText.includes(normalizedTitle)) {
          score += 3;
        }

        if (score > bestScore) {
          bestScore = score;
          bestMatch = variant;
        }
      });

      if (bestScore > 0) {
        return bestMatch;
      }
    }

    return variants[0] || null;
  }

  function findSelectedCardProductVariant(product, card) {
    const variants = product?.variants || [];
    if (!variants.length) {
      return null;
    }

    const selectedVariantId = normalizeVariantId(getCardVariantSelect(card)?.value);
    if (selectedVariantId) {
      const selectedVariant = variants.find(
        (variant) => normalizeVariantId(variant.id) === selectedVariantId
      );
      if (selectedVariant) {
        return selectedVariant;
      }
    }

    const selectedVariantLabel = normalizeLookupText(getSelectedCardVariantLabel(card));
    if (selectedVariantLabel) {
      const labelMatch = variants.find((variant) =>
        normalizeLookupText(formatVariantOptionLabel(variant.title, product.title)) === selectedVariantLabel
      );
      if (labelMatch) {
        return labelMatch;
      }
    }

    return variants[0] || null;
  }

  async function resolveBundleBasePriceFromAddedCards() {
    const syncTotal = resolveBundleBasePriceFromAddedCardsSync();
    if (Number.isFinite(syncTotal) && syncTotal > 0) {
      return syncTotal;
    }

    const addedCards = getAddedProductCards();

    if (!addedCards.length) {
      return null;
    }

    const lineTotals = await Promise.all(
      addedCards.map(async (card) => {
        const cardTitle = getCardTitle(card);
        const quantity =
          getProductCardQuantity(card.querySelector(QUANTITY_SELECTOR)) ||
          getQuantityFromAddedLabel(getCardAddButtonLabel(card)) ||
          1;
        const resolvedHandle = await resolveProductHandle(card, cardTitle).catch(() => null);
        const product = resolvedHandle ? await fetchProduct(resolvedHandle).catch(() => null) : null;
        const variant = product ? findSelectedCardProductVariant(product, card) : null;
        const variantPriceCents = Number(variant?.price);

        if (!Number.isFinite(variantPriceCents) || variantPriceCents < 0) {
          return null;
        }

        return variantPriceCents * quantity;
      })
    );

    if (lineTotals.some((lineTotal) => !Number.isFinite(lineTotal))) {
      return null;
    }

    const totalCents = lineTotals.reduce((sum, lineTotal) => sum + lineTotal, 0);
    return totalCents > 0 ? totalCents : null;
  }

  async function resolveBundleBasePriceCents() {
    const addedCardsTotal = await resolveBundleBasePriceFromAddedCards().catch(() => null);
    if (Number.isFinite(addedCardsTotal) && addedCardsTotal > 0) {
      return addedCardsTotal;
    }

    const cartItems = [...document.querySelectorAll('[data-skio-name="bab-cart-variant-item"]')];
    if (!cartItems.length) {
      return null;
    }

    const cartItemToCardMap = buildCartItemToCardMap();
    const lineTotals = await Promise.all(
      cartItems.map(async (cartItem) => {
        const matchedCard = cartItemToCardMap.get(cartItem) || null;
        const cardTitle = matchedCard ? getCardTitle(matchedCard) : '';
        const resolvedHandle =
          (matchedCard && await resolveProductHandle(matchedCard, cardTitle).catch(() => null)) ||
          getCartVariantProductHandle(cartItem);

        if (!resolvedHandle) {
          return null;
        }

        const product = await fetchProduct(resolvedHandle).catch(() => null);
        const variant = product ? findMatchingProductVariant(product, cartItem, matchedCard) : null;
        const variantPriceCents = Number(variant?.price);
        const quantity = getCartVariantQuantity(cartItem);

        if (!Number.isFinite(variantPriceCents) || variantPriceCents < 0) {
          return null;
        }

        return variantPriceCents * quantity;
      })
    );

    if (lineTotals.some((lineTotal) => !Number.isFinite(lineTotal))) {
      return null;
    }

    const totalCents = lineTotals.reduce((sum, lineTotal) => sum + lineTotal, 0);
    return totalCents > 0 ? totalCents : null;
  }

  function getRenderedOneTimePriceCents() {
    for (const scope of getFrequencyPriceScopes()) {
      const oneTimePrice = scope.querySelector('[data-skio-name="bab-cart-frequency-price-onetime"]');
      const storedBasePrice = Number(oneTimePrice?.dataset.subscriptionBasePriceCents);
      if (Number.isFinite(storedBasePrice) && storedBasePrice > 0) {
        return storedBasePrice;
      }

      const renderedStateBasePrice = String(oneTimePrice?.dataset.subscriptionPriceState || '').match(/:(\d+):\d+$/);
      if (renderedStateBasePrice) {
        const stateBasePrice = Number(renderedStateBasePrice[1]);
        if (Number.isFinite(stateBasePrice) && stateBasePrice > 0) {
          return stateBasePrice;
        }
      }

      if (oneTimePrice?.dataset.subscriptionPriceState) {
        continue;
      }
      const oneTimeCents = parseSubscriptionMoney(oneTimePrice?.textContent);
      if (Number.isFinite(oneTimeCents) && oneTimeCents > 0) {
        return oneTimeCents;
      }
    }

    return null;
  }

  let frequencyPriceSyncVersion = 0;
  let lastBundleBasePriceCents = null;
  let lastBundlePriceSignature = '';

  function getBundlePriceSignature() {
    return [...document.querySelectorAll('[data-skio-name="bab-cart-variant-item"]')]
      .map((item) => `${getCartVariantQuantity(item)}:${getCartVariantItemLookupText(item)}`)
      .join('|');
  }

  async function syncFrequencyPrices() {
    const syncVersion = ++frequencyPriceSyncVersion;
    const priceSignature = getBundlePriceSignature();
    const basePriceCents = await resolveBundleBasePriceCents().catch(() => null);

    if (syncVersion !== frequencyPriceSyncVersion) {
      return;
    }

    if (Number.isFinite(basePriceCents) && basePriceCents > 0) {
      lastBundleBasePriceCents = basePriceCents;
      lastBundlePriceSignature = priceSignature;
    }

    const cachedBasePriceCents =
      priceSignature && priceSignature === lastBundlePriceSignature ? lastBundleBasePriceCents : null;
    const oneTimeCents = basePriceCents || cachedBasePriceCents || getRenderedOneTimePriceCents();
    if (!Number.isFinite(oneTimeCents) || oneTimeCents <= 0) {
      return;
    }

    getFrequencyPriceScopes().forEach((scope) => {
      const subscriptionPrice = scope.querySelector(
        '[data-skio-name="bab-cart-frequency-price-subscription"]'
      );
      const oneTimePrice = scope.querySelector(
        '[data-skio-name="bab-cart-frequency-price-onetime"]'
      );

      if (!subscriptionPrice || !oneTimePrice) {
        return;
      }

      const oneTimeDiscountPercent = getOneTimeDiscountPercent();
      if (oneTimeDiscountPercent > 0) {
        const discountedOneTimeCents = Math.round(oneTimeCents * (100 - oneTimeDiscountPercent) / 100);
        if (
          Number.isFinite(discountedOneTimeCents) &&
          discountedOneTimeCents > 0 &&
          discountedOneTimeCents < oneTimeCents
        ) {
          renderOneTimeDiscountedFrequencyPrice(oneTimePrice, oneTimeCents, discountedOneTimeCents);
        } else {
          renderOneTimeFrequencyPrice(oneTimePrice, oneTimeCents);
        }
      } else {
        renderOneTimeFrequencyPrice(oneTimePrice, oneTimeCents);
      }

      renderOneTimeFrequencyPrice(subscriptionPrice, oneTimeCents);
    });
  }

  function getDefaultSubscriptionBenefits() {
    return [
      '<ul>',
      '<li>1 product = 10% off, 2 products = 15% off, 3+ products = 20% off</li>',
      '<li>Skip, adjust, or cancel anytime</li>',
      '<li>Free shipping on subscriptions and orders over $50</li>',
      '<li>Flexible delivery</li>',
      '<li>Shipping reminders</li>',
      '</ul>',
    ].join('');
  }

  function getSubscriptionBenefitsMarkup() {
    const configuredBenefits =
      typeof subscriptionBenefits === 'string' ? subscriptionBenefits.trim() : '';

    if (stripHtml(configuredBenefits).trim()) {
      return configuredBenefits;
    }

    return getDefaultSubscriptionBenefits();
  }

  function getSubscriptionBoxHeading() {
    return (typeof subscriptionBoxTitle !== 'undefined' && subscriptionBoxTitle)
      ? subscriptionBoxTitle
      : "<p>Your <em>custom</em> bundle</p>";
  }

  function getSubscriptionFrequencyHeading() {
    return (typeof subscriptionFrequencyTitle !== 'undefined' && subscriptionFrequencyTitle)
      ? subscriptionFrequencyTitle
      : 'Select frequency';
  }

  function getSubscriptionDiscountText() {
    const discountText = getDiscountTextForMode('subscription');

    if (discountText) {
      return discountText;
    }

    const discountBadge = document.querySelector('[data-skio-name="bab-cart-frequency-discount-badge"]');
    return discountBadge?.textContent.replace(/save/i, '').trim() || '';
  }

  function getOneTimeDiscountText() {
    return getDiscountTextForMode('onetime');
  }

  function getOneTimeBenefitsMarkup() {
    const steps = getConfiguredDiscountSteps('onetime');
    const meaningfulSteps = steps
      .map((step, index) => ({ step, index, percent: getDiscountPercentFromText(step) }))
      .filter((entry) => entry.percent > 0);

    if (!meaningfulSteps.length) {
      return '';
    }

    const labels = meaningfulSteps.map(({ step, index }) => {
      const itemLabel = index === steps.length - 1 ? `${index + 1}+ products` : `${index + 1} products`;
      return `${itemLabel} = ${step} off`;
    });

    return `<ul><li>${labels.join(', ')}</li></ul>`;
  }

  function getActiveDiscountText() {
    return isSubscriptionModeSelected() ? getSubscriptionDiscountText() : getOneTimeDiscountText();
  }

  function getRemainingItemsMessage(mode) {
    const steps = getConfiguredDiscountSteps(mode)
      .map((step, index) => ({
        threshold: index + 1,
        text: step,
        percent: getDiscountPercentFromText(step),
      }))
      .filter((entry) => entry.percent > 0);

    if (!steps.length) {
      return '';
    }

    const itemCount = getBoxItemCount();
    const nextStep = steps.find((entry) => entry.threshold > itemCount);

    if (!nextStep) {
      return '';
    }

    const remainingItems = nextStep.threshold - itemCount;
    const itemLabel = remainingItems === 1 ? 'item' : 'items';
    return `Add ${remainingItems} more ${itemLabel} to get ${nextStep.text} off!`;
  }

  function syncProgressMessages(purchaseMode = getSelectedPurchaseMode()) {
    const message = getRemainingItemsMessage(purchaseMode);
    const cartScopes = [document.querySelector(SKIO_CART_SELECTOR), document.querySelector(SKIO_MOBILE_CART_SELECTOR)]
      .filter(Boolean);

    cartScopes.forEach((scope) => {
      const candidates = [...scope.querySelectorAll('p, span, div')].filter((element) => {
        const text = (element.textContent || '').replace(/\s+/g, ' ').trim();
        return /Add \d+ more item/i.test(text) && /to get .*off!?/i.test(text);
      });

      candidates.forEach((element) => {
        if (!message) {
          element.hidden = true;
          return;
        }

        element.hidden = false;
        setTextContentIfChanged(element, message);
      });
    });
  }

  function getDiscountLabelForSlot(mode, slotIndex) {
    const steps = getConfiguredDiscountSteps(mode);
    if (!steps.length) {
      return '';
    }

    const discountText = steps[Math.min(Math.max(slotIndex, 0), steps.length - 1)] || '';
    return getDiscountPercentFromText(discountText) > 0 ? `${discountText} off` : '';
  }

  function addDiscountBadge() {
    if (typeof discountStep === 'undefined' || discountStep == null || discountStep === '') {
      return;
    }

    const REMOVE_BUTTON = '[data-skio-name="bab-cart-variant-remove-button"]';
    const VARIANTS_LIST = '[data-skio-name="bab-cart-variants-list"]';
    let updateTimeout = null;
    let isUpdatingBadges = false;
    let variantsObserver = null;

    function syncDiscountBadgeLabels() {
      const selectedMode = getSelectedPurchaseMode();
      const discountsteps = getConfiguredDiscountSteps(selectedMode);
      const variantsList = document.querySelectorAll(
        '[data-skio-name="bab-cart-variant-item"]'
      );

      // Tiers are quantity-based ("3+ products = 20% off"), so a line item's badge
      // must reflect cumulative units filled by it, not its position in the list.
      let cumulativeQuantity = 0;

      variantsList.forEach((variant) => {
        let discountLabelElement = variant.querySelector(
          '[data-skio-discount-badge="true"]'
        );

        if (!discountLabelElement) {
          discountLabelElement = document.createElement('div');
          discountLabelElement.setAttribute('data-skio-discount-badge', 'true');

          const badgeTarget = variant.firstElementChild || variant;
          badgeTarget.prepend(discountLabelElement);
        }

        const slotsFilledBefore = cumulativeQuantity;
        cumulativeQuantity += getCartVariantQuantity(variant);

        // Once an earlier item already reached the max tier, later items don't
        // unlock anything new, so their badge stays blank.
        const discountLabel =
          discountsteps.length > 0 && slotsFilledBefore < discountsteps.length
            ? getDiscountLabelForSlot(selectedMode, cumulativeQuantity - 1)
            : '';

        setTextContentIfChanged(discountLabelElement, discountLabel);
        setHiddenIfChanged(discountLabelElement, false);
        const nextDiscountEmptyState = discountLabel ? 'false' : 'true';
        if (discountLabelElement.dataset.skioDiscountEmpty !== nextDiscountEmptyState) {
          discountLabelElement.dataset.skioDiscountEmpty = nextDiscountEmptyState;
        }
      });
    }

    syncCartVariantDiscountBadges = syncDiscountBadgeLabels;

    function updateDiscountBadges() {
      isUpdatingBadges = true;

      try {
        syncDiscountBadgeLabels();
      } finally {
        isUpdatingBadges = false;
      }

      syncPurchaseModeState();
      syncFrequencyPrices();
      ensureDesktopCartStepper();
      syncMobileCartState();
      syncCartPlaceholders();
      syncProductCardCtaLayout();
    }

    function scheduleUpdateDiscountBadges() {
      clearTimeout(updateTimeout);
      updateTimeout = setTimeout(updateDiscountBadges, 300);
    }

    function observeVariantsList(container) {
      if (variantsObserver) {
        variantsObserver.disconnect();
      }

      variantsObserver = new MutationObserver(() => {
        if (isUpdatingBadges) {
          return;
        }

        scheduleUpdateDiscountBadges();
        scheduleSkioEnhancements();
      });

      variantsObserver.observe(container, {
        childList: true,
        subtree: true,
      });
    }

    document.body.addEventListener('click', (event) => {
      const affectsBundlePricing =
        event.target.closest(ADD_TO_BOX_BUTTON) ||
        event.target.closest(REMOVE_BUTTON) ||
        event.target.closest(CART_VARIANT_QUANTITY_DECREASE_SELECTOR) ||
        event.target.closest(CART_VARIANT_QUANTITY_INCREASE_SELECTOR) ||
        event.target.closest(PROXY_STEPPER_ACTION_SELECTOR);

      if (affectsBundlePricing) {
        scheduleUpdateDiscountBadges();
        scheduleSkioEnhancements();
        window.setTimeout(scheduleSkioEnhancements, 150);
      }
    });

    const interval = setInterval(() => {
      const variantsListContainer = document.querySelector(VARIANTS_LIST);

      if (variantsListContainer) {
        clearInterval(interval);
        observeVariantsList(variantsListContainer);
        updateDiscountBadges();
      }
    }, 100);
  }

  function addSubscriptionBenefits() {
    const subscriptionApps = document.querySelectorAll(SUBSCRIPTION_OPTION_SELECTOR);
    const benefitsMarkup = getSubscriptionBenefitsMarkup();

    if (!subscriptionApps.length || !benefitsMarkup) {
      return;
    }

    subscriptionApps.forEach((subscriptionApp) => {
      let subscriptionHeaderElement = subscriptionApp.querySelector('.subscription-header');
      if (!subscriptionHeaderElement) {
        subscriptionHeaderElement = document.createElement('div');
        subscriptionHeaderElement.className = 'subscription-header';
        subscriptionApp.insertAdjacentElement('afterbegin', subscriptionHeaderElement);
      }

      let subscriptionTitleElement = subscriptionHeaderElement.querySelector('.subscription-title');
      if (!subscriptionTitleElement) {
        subscriptionTitleElement = document.createElement('div');
        subscriptionTitleElement.className = 'subscription-title';
        subscriptionHeaderElement.appendChild(subscriptionTitleElement);
      }
      setTextContentIfChanged(subscriptionTitleElement, getSubscriptionFrequencyHeading());

      let subscriptionBadgeElement = subscriptionHeaderElement.querySelector('.subscription-badge');
      if (!subscriptionBadgeElement) {
        subscriptionBadgeElement = document.createElement('div');
        subscriptionBadgeElement.className = 'subscription-badge';
        subscriptionHeaderElement.appendChild(subscriptionBadgeElement);
      }
      const subscriptionDiscountText = getSubscriptionDiscountText();
      setTextContentIfChanged(
        subscriptionBadgeElement,
        subscriptionDiscountText ? `SAVE ${subscriptionDiscountText}` : ''
      );
      setHiddenIfChanged(subscriptionBadgeElement, !subscriptionDiscountText);

      let benefitElement = subscriptionApp.querySelector('.subscription-benefits');
      if (!benefitElement) {
        benefitElement = document.createElement('div');
        benefitElement.className = 'subscription-benefits';
        subscriptionApp.appendChild(benefitElement);
      }
      if (benefitElement.dataset.subscriptionBenefitsMarkup !== benefitsMarkup) {
        benefitElement.innerHTML = benefitsMarkup;
        benefitElement.dataset.subscriptionBenefitsMarkup = benefitsMarkup;
      }
      setHiddenIfChanged(benefitElement, !isSubscriptionModeSelected());
      markDiscountBenefits(benefitElement);
    });
  }

  function addCartHeading() {
    const title = getSubscriptionBoxHeading();

    if (!title) {
      return;
    }

    const cart = document.querySelector('[data-skio-name="cart"]');
    if (!cart) {
      return;
    }

    let block = cart.querySelector('.subscription-cart-header-block');
    if (block) {
      const heading = block.querySelector('.subscription-cart-heading');
      if (heading) {
        setInnerHtmlIfChanged(heading, title);
      }
      block.querySelector('.subscription-cart-step-label')?.remove();
      return;
    }

    block = document.createElement('div');
    block.className = 'subscription-cart-header-block';

    const heading = document.createElement('div');
    heading.className = 'subscription-cart-heading';
    setInnerHtmlIfChanged(heading, title);

    block.appendChild(heading);
    cart.insertAdjacentElement('afterbegin', block);
  }

  function addMobileCartHeading() {
    const mobileCart = document.querySelector(SKIO_MOBILE_CART_SELECTOR);
    const titleRow = mobileCart?.querySelector(SKIO_MOBILE_CART_TITLE_ROW_SELECTOR);

    if (!mobileCart || !titleRow) {
      return;
    }

    let heading = titleRow.querySelector('.subscription-mobile-cart-heading');
    if (!heading) {
      heading = document.createElement('div');
      heading.className = 'subscription-mobile-cart-heading';
      titleRow.insertAdjacentElement('afterbegin', heading);
    }
    setInnerHtmlIfChanged(heading, getSubscriptionBoxHeading());

    const detailsButton = titleRow.querySelector('[data-skio-name="bab-cart-mobile-details-button"]');
    if (detailsButton) {
      detailsButton.dataset.subscriptionToggle = 'true';
    }

    const selectionBadge = titleRow.querySelector('[data-skio-name="bab-cart-selection-badge"]');
    if (selectionBadge) {
      selectionBadge.dataset.skioMobileMeta = 'true';
    }

    const summary = [...titleRow.querySelectorAll('span')]
      .find((element) =>
        element !== selectionBadge &&
        /% off|add \d+ more item/i.test(element.textContent || '')
      );

    if (summary) {
      summary.dataset.skioDiscountSummary = 'true';
    }

    let instructions = mobileCart.querySelector(MOBILE_INSTRUCTIONS_SELECTOR);
    if (!instructions) {
      instructions = document.createElement('p');
      instructions.className = 'subscription-mobile-cart-instructions';
      instructions.setAttribute('data-subscription-mobile-instructions', 'true');
      titleRow.insertAdjacentElement('afterend', instructions);
    }

    setTextContentIfChanged(
      instructions,
      'Add 2 items to save more, or open the bundle to choose Subscribe or One-Time.'
    );
  }

  function buildMobileCartStepper() {
    const stepper = document.createElement('div');
    stepper.className = 'subscription-mobile-stepper';
    stepper.setAttribute('data-subscription-mobile-stepper', 'true');
    stepper.setAttribute('aria-label', 'Bundle progress');

    const track = document.createElement('div');
    track.className = 'subscription-mobile-stepper__track';

    MOBILE_STEPPER_TIERS.forEach((tier) => {
      const item = document.createElement('div');
      item.className = 'subscription-mobile-stepper__item';
      item.setAttribute('data-subscription-mobile-stepper-item', 'true');
      item.dataset.stepThreshold = String(tier.threshold);

      const marker = document.createElement('span');
      marker.className = 'subscription-mobile-stepper__marker';
      marker.textContent = tier.badge;
      marker.setAttribute('aria-hidden', 'true');

      const label = document.createElement('span');
      label.className = 'subscription-mobile-stepper__label';
      label.textContent = tier.label;

      item.append(marker, label);
      track.appendChild(item);
    });

    stepper.appendChild(track);
    return stepper;
  }

  function syncMobileCartStepper(stepper) {
    if (!stepper) {
      return;
    }

    const itemCount = getBoxItemCount();
    const mobileInstructions = document.querySelector(MOBILE_INSTRUCTIONS_SELECTOR);
    const activeTier = MOBILE_STEPPER_TIERS.reduce((current, tier) => (
      itemCount >= tier.threshold ? tier.threshold : current
    ), 0);
    const stepItems = [...stepper.querySelectorAll('[data-subscription-mobile-stepper-item="true"]')];

    stepper.dataset.itemCount = String(itemCount);
    stepper.dataset.activeTier = String(activeTier);

    if (mobileInstructions) {
      setHiddenIfChanged(mobileInstructions, itemCount > 0);
    }

    stepItems.forEach((item, index) => {
      const threshold = Number(item.dataset.stepThreshold || 0);
      const nextThreshold = MOBILE_STEPPER_TIERS[index + 1]?.threshold ?? null;

      item.dataset.stepState = itemCount >= threshold ? 'active' : 'inactive';

      if (nextThreshold == null) {
        item.removeAttribute('data-connector-state');
      } else {
        item.dataset.connectorState = itemCount >= nextThreshold ? 'active' : 'inactive';
      }

      if (activeTier === threshold && itemCount >= threshold) {
        item.setAttribute('aria-current', 'step');
      } else {
        item.removeAttribute('aria-current');
      }
    });
  }

  function ensureMobileCartStepper() {
    const mobileCart = document.querySelector(SKIO_MOBILE_CART_SELECTOR);
    const header = mobileCart?.querySelector(SKIO_MOBILE_CART_HEADER_SELECTOR);
    const titleRow = header?.querySelector(SKIO_MOBILE_CART_TITLE_ROW_SELECTOR);

    if (!mobileCart || !header) {
      return;
    }

    let stepper = header.querySelector(MOBILE_STEPPER_SELECTOR);
    if (!stepper) {
      stepper = buildMobileCartStepper();

      const instructions = header.querySelector(MOBILE_INSTRUCTIONS_SELECTOR);

      if (instructions) {
        instructions.insertAdjacentElement('afterend', stepper);
      } else if (titleRow) {
        titleRow.insertAdjacentElement('afterend', stepper);
      } else {
        header.insertAdjacentElement('afterbegin', stepper);
      }
    }

    syncMobileCartStepper(stepper);
  }

  function buildDesktopCartStepper() {
    const stepper = document.createElement('div');
    stepper.className = 'subscription-desktop-stepper';
    stepper.setAttribute('data-subscription-desktop-stepper', 'true');
    stepper.setAttribute('aria-label', 'Bundle progress');

    const track = document.createElement('div');
    track.className = 'subscription-desktop-stepper__track';

    STEPPER_TIERS.forEach((tier) => {
      const item = document.createElement('div');
      item.className = 'subscription-desktop-stepper__item';
      item.setAttribute('data-subscription-desktop-stepper-item', 'true');
      item.dataset.stepThreshold = String(tier.threshold);

      const marker = document.createElement('span');
      marker.className = 'subscription-desktop-stepper__marker';
      marker.textContent = tier.badge;
      marker.setAttribute('aria-hidden', 'true');

      const label = document.createElement('span');
      label.className = 'subscription-desktop-stepper__label';
      label.textContent = tier.label;

      item.append(marker, label);
      track.appendChild(item);
    });

    stepper.appendChild(track);
    return stepper;
  }

  function syncDesktopCartStepper(stepper) {
    if (!stepper) {
      return;
    }

    const itemCount = getBoxItemCount();
    const activeTier = STEPPER_TIERS.reduce((current, tier) => (
      itemCount >= tier.threshold ? tier.threshold : current
    ), 0);
    const stepItems = [...stepper.querySelectorAll('[data-subscription-desktop-stepper-item="true"]')];

    stepper.dataset.itemCount = String(itemCount);
    stepper.dataset.activeTier = String(activeTier);

    stepItems.forEach((item, index) => {
      const threshold = Number(item.dataset.stepThreshold || 0);
      const nextThreshold = STEPPER_TIERS[index + 1]?.threshold ?? null;
      const isActive = itemCount >= threshold;

      item.dataset.stepState = isActive ? 'active' : 'inactive';

      const marker = item.querySelector('.subscription-desktop-stepper__marker');
      if (marker) {
        marker.style.background = isActive ? '#fff' : '';
        marker.style.borderColor = isActive ? '#fff' : '';
        marker.style.color = isActive ? '#2A6553' : '';
        marker.style.opacity = isActive ? '1' : '';
      }

      const label = item.querySelector('.subscription-desktop-stepper__label');
      if (label) {
        label.style.color = isActive ? '#fff' : '';
        label.style.opacity = isActive ? '1' : '';
      }

      if (nextThreshold == null) {
        item.removeAttribute('data-connector-state');
      } else {
        item.dataset.connectorState = itemCount >= nextThreshold ? 'active' : 'inactive';
      }

      if (activeTier === threshold && itemCount >= threshold) {
        item.setAttribute('aria-current', 'step');
      } else {
        item.removeAttribute('aria-current');
      }
    });
  }

  let desktopStepperVariantsObserver = null;
  let cartPlaceholderObserver = null;

  function ensureDesktopCartStepper() {
    const cart = document.querySelector(SKIO_CART_SELECTOR);
    if (!cart) {
      return;
    }

    let stepper = cart.querySelector(DESKTOP_STEPPER_SELECTOR);
    if (!stepper) {
      stepper = buildDesktopCartStepper();

      const headerBlock = cart.querySelector('.subscription-cart-header-block');
      if (headerBlock) {
        headerBlock.insertAdjacentElement('afterend', stepper);
      } else {
        cart.insertAdjacentElement('afterbegin', stepper);
      }
    }

    syncDesktopCartStepper(stepper);
    watchCartVariantsForStepper(stepper);
  }

  function watchCartVariantsForStepper(stepper) {
    if (desktopStepperVariantsObserver) {
      return;
    }

    const variantsList = document.querySelector('[data-skio-name="bab-cart-variants-list"]');
    if (!variantsList) {
      return;
    }

    desktopStepperVariantsObserver = new MutationObserver(() => {
      syncDesktopCartStepper(stepper);
    });

    desktopStepperVariantsObserver.observe(variantsList, {
      childList: true,
      subtree: true,
    });
  }

  function watchVariantsListForPlaceholders() {
    if (cartPlaceholderObserver) {
      return;
    }

    const variantsList = document.querySelector('[data-skio-name="bab-cart-variants-list"]');
    if (!variantsList) {
      return;
    }

    cartPlaceholderObserver = new MutationObserver(() => {
      syncCartPlaceholders();
    });

    cartPlaceholderObserver.observe(variantsList, {
      childList: true,
      subtree: true,
    });
  }

  function getBoxItemCount() {
    // Discount tiers are quantity-based (Skio applies a higher tier as line
    // quantities rise, not just when a distinct product is added), so count
    // total units per line rather than the number of line items.
    const desktopCart = document.querySelector(SKIO_CART_SELECTOR);
    const desktopVariantItems = desktopCart
      ? desktopCart.querySelectorAll('[data-skio-name="bab-cart-variant-item"]')
      : [];
    if (desktopVariantItems.length > 0) {
      return [...desktopVariantItems].reduce(
        (total, item) => total + getCartVariantQuantity(item),
        0
      );
    }

    const mobileCart = document.querySelector(SKIO_MOBILE_CART_SELECTOR);
    if (!mobileCart || !isElementVisible(mobileCart)) {
      return 0;
    }

    const mobileThumbnailCount = [...mobileCart.querySelectorAll(
      '[data-skio-name="bab-cart-mobile-thumbnail-remove"]'
    )].filter((thumbnailRemove) => isElementVisible(thumbnailRemove)).length;

    return mobileThumbnailCount;
  }

  function setupPurchaseModeListeners() {
    if (!document.body || document.body.dataset.subscriptionPurchaseModeListenerAttached === 'true') {
      return;
    }

    document.body.dataset.subscriptionPurchaseModeListenerAttached = 'true';

    const getEventPurchaseMode = (target) => {
      if (target.closest(SUBSCRIPTION_OPTION_SELECTOR)) {
        return 'subscription';
      }

      if (target.closest(ONETIME_OPTION_SELECTOR)) {
        return 'onetime';
      }

      return null;
    };

    const resyncPurchaseMode = () => {
      scheduleSkioEnhancements();
      window.setTimeout(() => {
        scheduleSkioEnhancements();
      }, 120);
    };

    document.body.addEventListener('change', (event) => {
      const mode = getEventPurchaseMode(event.target);
      if (mode) {
        purchaseModeOverride = mode;
        applyPurchaseModeState(mode);
        resyncPurchaseMode();
      }
    }, true);

    document.body.addEventListener('click', (event) => {
      const mode = getEventPurchaseMode(event.target);
      if (mode) {
        purchaseModeOverride = mode;
        applyPurchaseModeState(mode);
        resyncPurchaseMode();
        return;
      }

      if (event.target.closest('[data-skio-name="bab-cart-mobile-details-button"]')) {
        resyncPurchaseMode();
      }
    }, true);
  }

  function isOneTimeCheckoutTrigger(target) {
    const action = target.closest('button, a, [role="button"], input[type="submit"], input[type="button"]');
    if (!action) {
      return null;
    }

    const cartScope = action.closest(`${SKIO_CART_SELECTOR}, ${SKIO_MOBILE_CART_SELECTOR}`);
    if (!cartScope) {
      return null;
    }

    if (action.closest('[data-skio-name="bab-cart-variant-remove-button"], [data-skio-name="bab-cart-variant-quantity-controls"]')) {
      return null;
    }

    const label = (
      action.getAttribute('aria-label') ||
      action.getAttribute('value') ||
      action.textContent ||
      ''
    ).replace(/\s+/g, ' ').trim();
    const href = action.getAttribute('href') || '';

    if (/checkout|next|continue/i.test(label) || /\/checkout\b/i.test(href)) {
      return action;
    }

    return null;
  }

  async function resolveOneTimeCartLineFromItem(cartItem, cartItemToCardMap) {
    const quantity = getCartVariantQuantity(cartItem);
    const matchedCard = cartItemToCardMap.get(cartItem) || null;
    const cardTitle = matchedCard ? getCardTitle(matchedCard) : '';
    const resolvedHandle =
      (matchedCard && await resolveProductHandle(matchedCard, cardTitle).catch(() => null)) ||
      getCartVariantProductHandle(cartItem);

    const product = resolvedHandle ? await fetchProduct(resolvedHandle).catch(() => null) : null;
    const variant = product ? findMatchingProductVariant(product, cartItem, matchedCard) : null;
    const fallbackVariantId = !variant ? Number(getCartVariantIdCandidates(cartItem)[0]) : null;
    const variantId = Number(normalizeVariantId(variant?.id || fallbackVariantId));

    if (!Number.isFinite(variantId) || variantId <= 0 || variantId < 1000000) {
      return null;
    }

    return {
      id: variantId,
      quantity,
    };
  }

  async function resolveOneTimeCartLineFromCard(card) {
    const cardTitle = getCardTitle(card);
    const resolvedHandle = await resolveProductHandle(card, cardTitle).catch(() => null);
    const product = resolvedHandle ? await fetchProduct(resolvedHandle).catch(() => null) : null;
    const variant = product ? findSelectedCardProductVariant(product, card) : null;
    const variantId = Number(normalizeVariantId(variant?.id));

    if (!Number.isFinite(variantId) || variantId <= 0) {
      return null;
    }

    return {
      id: variantId,
      quantity:
        getProductCardQuantity(card.querySelector(QUANTITY_SELECTOR)) ||
        getQuantityFromAddedLabel(getCardAddButtonLabel(card)) ||
        1,
    };
  }

  async function buildOneTimeCheckoutItems() {
    const cartItems = [...document.querySelectorAll('[data-skio-name="bab-cart-variant-item"]')];
    const cartItemToCardMap = buildCartItemToCardMap();
    const lineItems = cartItems.length
      ? await Promise.all(cartItems.map((cartItem) => resolveOneTimeCartLineFromItem(cartItem, cartItemToCardMap)))
      : await Promise.all(getAddedProductCards().map(resolveOneTimeCartLineFromCard));

    const itemsByVariantId = new Map();
    lineItems.filter(Boolean).forEach((item) => {
      const variantId = Number(item.id);
      const quantity = Number(item.quantity) || 1;
      if (!Number.isFinite(variantId) || variantId <= 0 || quantity <= 0) {
        return;
      }

      itemsByVariantId.set(variantId, (itemsByVariantId.get(variantId) || 0) + quantity);
    });

    return [...itemsByVariantId.entries()].map(([id, quantity]) => ({ id, quantity }));
  }

  function setOneTimeCheckoutLoading(action, isLoading) {
    if (!action) {
      return;
    }

    if (isLoading) {
      action.dataset.subscriptionOriginalText = action.textContent || '';
      action.setAttribute('aria-busy', 'true');
      if ('disabled' in action) {
        action.disabled = true;
      }
      if (action.textContent) {
        action.textContent = 'CHECKING OUT...';
      }
    } else {
      action.removeAttribute('aria-busy');
      if ('disabled' in action) {
        action.disabled = false;
      }
      if (action.dataset.subscriptionOriginalText) {
        action.textContent = action.dataset.subscriptionOriginalText;
      }
    }
  }

  async function checkoutOneTimeThroughShopify(action) {
    const items = await buildOneTimeCheckoutItems();

    if (!items.length) {
      return false;
    }

    setOneTimeCheckoutLoading(action, true);

    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({ items }),
    });

    if (!response.ok) {
      setOneTimeCheckoutLoading(action, false);
      return false;
    }

    window.location.assign('/checkout');
    return true;
  }

  function setupOneTimeCheckoutBypass() {
    if (!document.body || document.body.dataset.subscriptionOneTimeCheckoutBypassAttached === 'true') {
      return;
    }

    document.body.dataset.subscriptionOneTimeCheckoutBypassAttached = 'true';

    document.body.addEventListener('click', (event) => {
      if (getSelectedPurchaseMode() !== 'onetime') {
        return;
      }

      const action = isOneTimeCheckoutTrigger(event.target);
      if (!action) {
        return;
      }

      event.preventDefault();
      event.stopImmediatePropagation();

      checkoutOneTimeThroughShopify(action).then((handled) => {
        if (!handled) {
          setOneTimeCheckoutLoading(action, false);
          window.alert('We could not prepare your one-time bundle for checkout. Please refresh and try again.');
        }
      }).catch(() => {
        setOneTimeCheckoutLoading(action, false);
        window.alert('We could not prepare your one-time bundle for checkout. Please refresh and try again.');
      });
    }, true);
  }

  function setupCartQuantityGuard() {
    if (!document.body || document.body.dataset.subscriptionCartQuantityGuardAttached === 'true') {
      return;
    }

    document.body.dataset.subscriptionCartQuantityGuardAttached = 'true';

    const handleQuantityGuardInteraction = (event) => {
      if (
        event.type === 'keydown' &&
        event.key !== 'Enter' &&
        event.key !== ' '
      ) {
        return;
      }

      // Detect Skio's native decrease button via its own explicit attribute
      // first — confirmed live against production markup, present the
      // instant Skio renders the button with zero dependency on our own
      // debounced sync pass. This closes the race window that let clicks
      // through in the moment right after "Add", before our sync had a
      // chance to swap in the safe proxy stepper (verified live: Skio's
      // native decrease-at-quantity-1 button does a full document reload
      // instead of an in-place update, which is what actually crashes under
      // real-world network/script load). Fall back to structural detection
      // (first of the two buttons) only if that attribute isn't present, in
      // case Skio's markup changes.
      const nativeDecreaseControl = event.target.closest(
        `${SKIO_CARD_SELECTOR} ${NATIVE_QUANTITY_DECREASE_SELECTOR}`
      );
      const quantitySelectorAncestor = !nativeDecreaseControl
        ? event.target.closest(`${SKIO_CARD_SELECTOR} ${QUANTITY_SELECTOR}`)
        : null;
      const productCardDecreaseControl =
        nativeDecreaseControl ||
        (quantitySelectorAncestor
          ? getProductCardQuantitySelectorControl(quantitySelectorAncestor, 'decrease')
          : null);

      if (productCardDecreaseControl?.contains(event.target)) {
        const productCard = productCardDecreaseControl.closest(SKIO_CARD_SELECTOR);
        const matchedCartItemCandidates = findAllMatchingCartVariantItems(productCard);
        if (
          rerouteQuantityDecreaseToRemove(
            event,
            matchedCartItemCandidates,
            productCardDecreaseControl
          )
        ) {
          return;
        }
      }

      const cartItem = event.target.closest('[data-skio-name="bab-cart-variant-item"]');
      if (!cartItem) {
        return;
      }

      const decreaseControl = getCartVariantQuantityControl(cartItem, 'decrease');
      rerouteQuantityDecreaseToRemove(event, [cartItem], decreaseControl);
    };

    // Skio's own decrease/remove controls are wired to the native `click`
    // event. `pointerdown` fires earlier but is a *separate* event from
    // `click` — calling preventDefault()/stopImmediatePropagation() on a
    // pointerdown does not stop the click that follows it for mouse input,
    // so a pointerdown-based guard never actually reaches Skio's handler in
    // time. Listening for `click` in the capture phase on document.body runs
    // before Skio's listener regardless of where in the tree it's attached
    // (Skio's root is always a descendant of body), so this reliably
    // intercepts the click before Skio ever sees it. `keydown` is kept as a
    // belt-and-suspenders guard for non-native `[role="button"]` controls
    // that may act on keydown directly instead of relying on a synthesized
    // click.
    document.body.addEventListener('click', handleQuantityGuardInteraction, true);
    document.body.addEventListener('keydown', handleQuantityGuardInteraction, true);
  }

  function isMobileViewport() {
    return window.matchMedia('(max-width: 768px)').matches;
  }

  function isElementVisible(element) {
    if (!element) {
      return false;
    }

    const style = window.getComputedStyle(element);
    if (
      style.display === 'none' ||
      style.visibility === 'hidden' ||
      element.hidden
    ) {
      return false;
    }

    const rect = element.getBoundingClientRect();
    return rect.width > 1 && rect.height > 1;
  }

  function getCartVariantItemScopes() {
    const mobileCart = document.querySelector(SKIO_MOBILE_CART_SELECTOR);
    const desktopCart = document.querySelector(SKIO_CART_SELECTOR);
    const preferredScopes = isMobileViewport()
      ? [mobileCart, desktopCart]
      : [desktopCart, mobileCart];
    const scopes = [];

    preferredScopes.forEach((scope) => {
      if (scope && !scopes.includes(scope)) {
        scopes.push(scope);
      }
    });

    [desktopCart, mobileCart].forEach((scope) => {
      if (scope && !scopes.includes(scope)) {
        scopes.push(scope);
      }
    });

    scopes.push(document);
    return scopes;
  }

  function isPurchaseModeOptionSelected(option) {
    if (!option) {
      return false;
    }

    const selectionScope = option.querySelector(FREQUENCY_CONTENT_SELECTOR) || option;
    const radio = option.querySelector('input[type="radio"]');
    if (radio?.checked) {
      return true;
    }

    const selectedStateSelectors = [
      '[data-selected="true"]',
      '[aria-checked="true"]',
      '[aria-selected="true"]',
      '[data-state="checked"]',
      '[data-state="on"]',
      '[data-headlessui-state~="checked"]',
    ].join(', ');

    return (
      Boolean(option.matches?.(selectedStateSelectors)) ||
      Boolean(selectionScope.matches?.(selectedStateSelectors)) ||
      Boolean(selectionScope.querySelector?.(selectedStateSelectors)) ||
      Boolean(selectionScope.querySelector?.('.ring-primary .bg-brand-500, .bg-brand-500.size-1\\.5'))
    );
  }

  function getPurchaseModeScopes() {
    const mobileCart = document.querySelector(SKIO_MOBILE_CART_SELECTOR);
    const desktopCart = document.querySelector(SKIO_CART_SELECTOR);
    // Skio renders separate desktop and mobile carts, so prefer the active viewport's cart first.
    const preferredScopes = isMobileViewport()
      ? [mobileCart, desktopCart]
      : [desktopCart, mobileCart];
    const scopes = [];

    preferredScopes.forEach((scope) => {
      if (scope && !scopes.includes(scope)) {
        scopes.push(scope);
      }
    });

    [desktopCart, mobileCart].forEach((scope) => {
      if (scope && !scopes.includes(scope)) {
        scopes.push(scope);
      }
    });

    scopes.push(document);
    return scopes;
  }

  function getSelectedPurchaseModeFromScope(scope) {
    if (!scope?.querySelector) {
      return null;
    }

    const onetimeOption = scope.querySelector(ONETIME_OPTION_SELECTOR);
    const subscriptionOption = scope.querySelector(SUBSCRIPTION_OPTION_SELECTOR);

    if (!onetimeOption && !subscriptionOption) {
      return null;
    }

    if (isPurchaseModeOptionSelected(onetimeOption)) {
      return 'onetime';
    }

    if (isPurchaseModeOptionSelected(subscriptionOption)) {
      return 'subscription';
    }

    return null;
  }

  function getDetectedPurchaseMode() {
    const scopes = getPurchaseModeScopes();
    const visibleScopes = scopes.filter((scope) => scope !== document && isElementVisible(scope));

    for (const scope of [...visibleScopes, ...scopes]) {
      const mode = getSelectedPurchaseModeFromScope(scope);
      if (mode) {
        return mode;
      }
    }

    return null;
  }

  function getSelectedPurchaseMode() {
    const detectedMode = getDetectedPurchaseMode();

    if (detectedMode) {
      // DOM state is known — sync the override so it never contradicts reality.
      purchaseModeOverride = detectedMode;
      return detectedMode;
    }

    // DOM state is ambiguous (e.g. Skio is mid-render); fall back to last known override.
    if (purchaseModeOverride) {
      return purchaseModeOverride;
    }

    return 'subscription';
  }

  function isSubscriptionModeSelected() {
    return getSelectedPurchaseMode() === 'subscription';
  }

  function markDiscountBenefits(container) {
    if (!container) {
      return;
    }

    container.querySelectorAll('li, p').forEach((element) => {
      const text = element.textContent || '';
      const isDiscountCopy = /% off|save \d+/i.test(text);
      element.dataset.subscriptionDiscountBenefit = isDiscountCopy ? 'true' : 'false';
    });
  }

  function syncSubscriptionHeaderBadge() {
    const discountText = getSubscriptionDiscountText();
    const isSubscription = isSubscriptionModeSelected();

    document.querySelectorAll('.subscription-badge').forEach((badge) => {
      setTextContentIfChanged(badge, discountText ? `SAVE ${discountText}` : '');
      setHiddenIfChanged(badge, !isSubscription || !discountText);
    });
  }

  function syncOneTimeHeaderBadge() {
    const discountText = getOneTimeDiscountText();

    document.querySelectorAll(ONETIME_OPTION_SELECTOR).forEach((option) => {
      const content = option.querySelector(FREQUENCY_CONTENT_SELECTOR) || option;
      let badge = option.querySelector('.subscription-onetime-badge');
      if (!badge) {
        badge = document.createElement('span');
        badge.className = 'subscription-onetime-badge';

        const label = option.querySelector('[data-skio-name="bab-cart-frequency-details-onetime"] p') ||
          option.querySelector('[data-skio-name="bab-cart-frequency-label"] p') ||
          option.querySelector('[data-skio-name="bab-cart-frequency-label"]');
        if (label) {
          label.insertAdjacentElement('afterend', badge);
        } else {
          content.insertAdjacentElement('afterbegin', badge);
        }
      }

      setTextContentIfChanged(badge, discountText ? `SAVE ${discountText}` : '');
      setHiddenIfChanged(badge, !discountText);
    });
  }

  function syncOneTimeBenefits() {
    const benefitsMarkup = getOneTimeBenefitsMarkup();

    document.querySelectorAll(ONETIME_OPTION_SELECTOR).forEach((option) => {
      let benefitElement = option.querySelector('.subscription-onetime-benefits');
      if (!benefitElement) {
        benefitElement = document.createElement('div');
        benefitElement.className = 'subscription-onetime-benefits';
        option.appendChild(benefitElement);
      }

      if (benefitElement.dataset.subscriptionBenefitsMarkup !== benefitsMarkup) {
        benefitElement.innerHTML = benefitsMarkup;
        benefitElement.dataset.subscriptionBenefitsMarkup = benefitsMarkup;
      }

      setHiddenIfChanged(benefitElement, !isSubscriptionModeSelected() && !benefitsMarkup ? true : isSubscriptionModeSelected());
    });
  }

  function ensurePurchaseModeIcons() {
    [
      {
        selector: SUBSCRIPTION_OPTION_SELECTOR,
        priceSelector: '[data-skio-name="bab-cart-frequency-price-subscription"]',
      },
      {
        selector: ONETIME_OPTION_SELECTOR,
        priceSelector: '[data-skio-name="bab-cart-frequency-price-onetime"]',
      },
    ].forEach(({ selector, priceSelector }) => {
      document.querySelectorAll(selector).forEach((option) => {
        const content = option.querySelector(FREQUENCY_CONTENT_SELECTOR);
        if (!content) {
          return;
        }

        let icon = option.querySelector(`.${PURCHASE_MODE_ICON_CLASS}`);
        if (!icon) {
          icon = document.createElement('span');
          icon.className = PURCHASE_MODE_ICON_CLASS;
          icon.setAttribute('aria-hidden', 'true');

          const price = content.querySelector(priceSelector);
          if (price && price.parentElement === content) {
            price.insertAdjacentElement('afterend', icon);
          } else {
            content.appendChild(icon);
          }
        }
      });
    });
  }

  function syncPurchaseModeIcons(purchaseMode) {
    [
      {
        selector: SUBSCRIPTION_OPTION_SELECTOR,
        mode: 'subscription',
      },
      {
        selector: ONETIME_OPTION_SELECTOR,
        mode: 'onetime',
      },
    ].forEach(({ selector, mode }) => {
      document.querySelectorAll(selector).forEach((option) => {
        option.dataset.subscriptionAccordionState = purchaseMode === mode ? 'expanded' : 'collapsed';
      });
    });
  }

  function syncMobileCartState() {
    const mobileCart = document.querySelector(SKIO_MOBILE_CART_SELECTOR);
    if (!mobileCart) {
      return;
    }

    const detailsButton = mobileCart.querySelector('[data-skio-name="bab-cart-mobile-details-button"]');
    const expandedPanel = mobileCart.querySelector('[data-skio-name="bab-cart-mobile-expanded"]');
    const expandedWrap = expandedPanel?.parentElement;

    let isExpanded = false;

    if (expandedPanel) {
      const panelStyle = window.getComputedStyle(expandedPanel);
      const wrapStyle = expandedWrap ? window.getComputedStyle(expandedWrap) : null;
      const panelHidden =
        expandedPanel.hidden ||
        expandedPanel.getAttribute('aria-hidden') === 'true' ||
        panelStyle.display === 'none' ||
        panelStyle.visibility === 'hidden';
      const wrapCollapsed =
        /\bgrid-rows-\[0fr\]\b/.test(expandedWrap?.className || '') ||
        wrapStyle?.display === 'none' ||
        (expandedWrap ? expandedWrap.getBoundingClientRect().height <= 1 : false);
      const panelVisible = expandedPanel.getBoundingClientRect().height > 1;

      isExpanded = !panelHidden && !wrapCollapsed && panelVisible;

      if (!isExpanded && detailsButton?.hasAttribute('aria-expanded')) {
        isExpanded = detailsButton.getAttribute('aria-expanded') === 'true';
      }
    } else if (detailsButton?.hasAttribute('aria-expanded')) {
      isExpanded = detailsButton.getAttribute('aria-expanded') === 'true';
    }

    mobileCart.dataset.expanded = isExpanded ? 'true' : 'false';
    mobileCart.dataset.purchaseMode = getSelectedPurchaseMode();
  }

  function isSubscriptionSecondStepActive() {
    return Boolean(document.querySelector('button[aria-label="Go to previous stage"]'));
  }

  function syncFilterBlockVisibility() {
    const filterContainer = document.querySelector('.filter-list-container');
    if (!filterContainer) {
      return;
    }

    filterContainer.hidden = isSubscriptionSecondStepActive();
  }

  function setupFilterBlockVisibilityObserver() {
    if (filterBlockVisibilityObserver || !document.body) {
      return;
    }

    filterBlockVisibilityObserver = new MutationObserver(() => {
      syncFilterBlockVisibility();
      syncStepHeadings();
    });

    filterBlockVisibilityObserver.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['aria-label'],
    });
  }

  function applyPurchaseModeState(purchaseMode) {
    const root = getSkioRoot();
    const mobileCart = document.querySelector(SKIO_MOBILE_CART_SELECTOR);

    document.documentElement.classList.toggle('subscription-mode-subscription', purchaseMode === 'subscription');
    document.documentElement.classList.toggle('subscription-mode-onetime', purchaseMode === 'onetime');

    if (root) {
      root.dataset.purchaseMode = purchaseMode;
    }

    if (mobileCart) {
      mobileCart.dataset.purchaseMode = purchaseMode;
    }

    syncSubscriptionHeaderBadge();
    syncOneTimeHeaderBadge();
    syncOneTimeBenefits();
    syncSubscriptionComparePricing();
    syncProgressMessages(purchaseMode);
    syncPurchaseModeIcons(purchaseMode);

    document.querySelectorAll('[data-subscription-discount-benefit="true"]').forEach((element) => {
      element.hidden = purchaseMode === 'onetime';
    });

    document.querySelectorAll('[data-skio-discount-summary="true"]').forEach((element) => {
      const discountText = getActiveDiscountText();
      element.hidden = !discountText;
      setTextContentIfChanged(element, discountText ? `Save ${discountText}` : '');
    });

    document.querySelectorAll('.subscription-benefits').forEach((element) => {
      element.hidden = purchaseMode === 'onetime';
    });

    document.querySelectorAll(SUBSCRIPTION_OPTION_SELECTOR).forEach((option) => {
      // Only control our own injected elements (.subscription-benefits).
      // bab-cart-interval-select is Skio's native selling plan selector — let Skio manage its own visibility.
      option.querySelectorAll('.subscription-benefits').forEach((element) => {
        element.hidden = purchaseMode === 'onetime';
        element.setAttribute('aria-hidden', purchaseMode === 'onetime' ? 'true' : 'false');
        element.style.display = purchaseMode === 'onetime' ? 'none' : '';
      });
    });

    syncCartVariantDiscountBadges();
    syncCartPlaceholders();
    syncProgressMessages(purchaseMode);
  }

  function syncPurchaseModeState() {
    applyPurchaseModeState(getSelectedPurchaseMode());
  }



  function attachDetailButtons() {
    TITLE_SELECTORS.forEach((selector) => {
      document.querySelectorAll(selector).forEach((title) => {
        if (title.dataset.detailsButtonAttached === 'true') {
          return;
        }

        title.dataset.detailsButtonAttached = 'true';

        const button = document.createElement('button');
        button.type = 'button';
        button.innerText = 'View details';
        button.classList.add('product-card-details-button');
        button.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          openModal(findProductCard(title));
        });

        const card = findProductCard(title);
        const cardActions =
          card?.querySelector('[data-skio-name="product-card-actions"]') ||
          card?.querySelector('[data-skio-name="product-card-footer"]');

        if (cardActions) {
          cardActions.insertAdjacentElement('afterbegin', button);
        } else {
          title.insertAdjacentElement('afterend', button);
        }
      });
    });
  }

  function normalizeFilterValue(value) {
    return String(value || '')
      .trim()
      .toLowerCase();
  }

  function normalizeProductLookupKey(value) {
    return String(value || '')
      .trim()
      .toLowerCase()
      .replace(/\s+/g, ' ');
  }

  function splitFilterString(value) {
    return String(value || '')
      .replace(/<[^>]*>/g, ' ')
      .split(/[,;\n\r|]+/)
      .map(normalizeFilterValue)
      .filter(Boolean);
  }

  function getProductFilterValues(productData) {
    const filters = productData?.filters;
    if (!filters) {
      return [];
    }

    const filterList = Array.isArray(filters) ? filters : [filters];

    return filterList
      .flatMap((entry) => {
        if (typeof entry === 'string') {
          return splitFilterString(entry);
        }

        if (entry && typeof entry === 'object') {
          return splitFilterString(entry.value || entry.label || entry.name || '');
        }

        return splitFilterString(entry);
      })
      .filter(Boolean);
  }

  function productMatchesFilter(productData, filterValue) {
    if (normalizeFilterValue(filterValue) === 'view all') {
      return true;
    }

    if (!productData) {
      return false;
    }

    const selectedFilter = normalizeFilterValue(filterValue);
    return getProductFilterValues(productData).includes(selectedFilter);
  }

  function setupProductFilters() {
    const filterContainer = document.querySelector('.filter-list-container');
    const filterItems = filterContainer
      ? [...filterContainer.querySelectorAll('.filter-item')]
      : [];
    const resetButton = filterContainer?.querySelector('.filter-reset-button');
    const filterToggle = filterContainer?.querySelector('.filter-list-toggle');

    if (!filterItems.length) {
      return;
    }

    const defaultFilterItem = getDefaultFilterItem(filterItems);
    const defaultFilter =
      defaultFilterItem?.dataset.filter || defaultFilterItem?.textContent.trim() || 'VIEW ALL';

    activeProductFilter = defaultFilter;
    filterItems.forEach((filterItem) => {
      filterItem.classList.toggle('is-active', filterItem === defaultFilterItem);
    });

    const activateFilterItem = (item) => {
      if (!item) {
        activeProductFilter = 'VIEW ALL';
        filterItems.forEach((filterItem) => {
          filterItem.classList.remove('is-active');
        });
        applyProductFilter();
        return;
      }

      activeProductFilter = item.dataset.filter || item.textContent.trim();
      filterItems.forEach((filterItem) => {
        filterItem.classList.toggle('is-active', filterItem === item);
      });

      applyProductFilter();
    };

    filterItems.forEach((item) => {
      if (item.dataset.filterAttached === 'true') {
        return;
      }

      item.dataset.filterAttached = 'true';

      const activateFilter = () => {
        activateFilterItem(item);
      };

      item.addEventListener('click', activateFilter);
      item.addEventListener('keydown', (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          activateFilter();
        }
      });
    });

    if (resetButton && resetButton.dataset.filterAttached !== 'true') {
      resetButton.dataset.filterAttached = 'true';
      resetButton.addEventListener('click', () => {
        activateFilterItem(defaultFilterItem);
      });
    }

    if (filterToggle && filterContainer && filterToggle.dataset.filterAttached !== 'true') {
      filterToggle.dataset.filterAttached = 'true';
      filterToggle.addEventListener('click', () => {
        if (window.matchMedia('(min-width: 769px)').matches) {
          return;
        }

        const isOpen = filterContainer.classList.toggle('is-open');
        filterToggle.setAttribute('aria-expanded', String(isOpen));
      });
    }

    applyProductFilter();
  }

  function getDefaultFilterItem(filterItems) {
    return [...filterItems].find(
      (item) =>
        normalizeFilterValue(item.dataset.filter || item.textContent.trim()) === 'view all'
    ) || filterItems[0] || null;
  }

  function getActiveProductGrid() {
    const grids = getProductGrids();
    return grids.find(isElementVisible) || grids[0] || null;
  }

  function getProductGrids() {
    return [...document.querySelectorAll(`${SKIO_PRODUCT_GRID_SELECTOR}, ${SKIO_UPSELL_GRID_SELECTOR}`)];
  }

  function isElementVisible(element) {
    if (!element || element.hidden) {
      return false;
    }

    const style = window.getComputedStyle(element);
    return style.display !== 'none' && style.visibility !== 'hidden';
  }

  function getGridCardItem(card, grid) {
    // wrapProductCardCollectionsInLists() wraps each card in its own
    // '.subscription-product-card-list-item' <li> inside a shared <ul>, so
    // that per-card wrapper — not the <ul> — is the real "one item per
    // card" boundary. Walking up to "parentElement === grid" would land on
    // the <ul> instead, which is shared by every card in the grid and would
    // make them all show/hide together as one block.
    const listItem = card.closest('.subscription-product-card-list-item');
    if (listItem) {
      return listItem;
    }

    if (!grid) {
      return card;
    }

    let node = card;

    while (node && node.parentElement && node.parentElement !== grid) {
      node = node.parentElement;
    }

    return node && node.parentElement === grid ? node : card;
  }

  function setFilterVisibility(element, isVisible) {
    if (!element) {
      return;
    }

    element.toggleAttribute('hidden', !isVisible);
    element.style.display = isVisible ? '' : 'none';
  }

  function ensureFilterEmptyState(targetGrid = getActiveProductGrid()) {
    const grid = targetGrid;
    if (!grid) {
      return null;
    }

    let emptyState = document.querySelector(FILTER_EMPTY_STATE_SELECTOR);
    if (!emptyState) {
      emptyState = document.createElement('div');
      emptyState.className = 'subscription-filter-empty-state';
      emptyState.setAttribute('data-subscription-filter-empty-state', 'true');
      emptyState.hidden = true;
      emptyState.innerHTML = `
        <p class="subscription-filter-empty-state__title">No products match this benefit yet.</p>
        <p class="subscription-filter-empty-state__copy">Try another filter or reset to view the full ritual.</p>
      `;
    }

    const parent = grid.parentElement;
    if (parent && emptyState.parentElement !== parent) {
      parent.insertBefore(emptyState, grid);
    } else if (parent && emptyState.nextElementSibling !== grid) {
      parent.insertBefore(emptyState, grid);
    }

    if (parent && window.getComputedStyle(parent).display.includes('grid')) {
      const gridStyle = window.getComputedStyle(grid);
      emptyState.style.gridColumn = gridStyle.gridColumn;
      emptyState.style.gridRow = gridStyle.gridRow;
    } else {
      emptyState.style.removeProperty('grid-column');
      emptyState.style.removeProperty('grid-row');
    }

    return emptyState;
  }

  function applyProductFilter() {
    const showAll = normalizeFilterValue(activeProductFilter) === 'view all';
    const grids = getProductGrids();
    const cards = [...document.querySelectorAll(SKIO_CARD_SELECTOR)];
    const fallbackGrid = getActiveProductGrid();
    const existingEmptyState = document.querySelector(FILTER_EMPTY_STATE_SELECTOR);

    if (!cards.length) {
      grids.forEach((grid) => {
        grid.classList.remove(FILTER_EMPTY_GRID_CLASS);
      });
      if (existingEmptyState) {
        existingEmptyState.hidden = true;
      }
      return true;
    }

    const cardStates = cards.map((card) => {
      const grid = card.closest(`${SKIO_PRODUCT_GRID_SELECTOR}, ${SKIO_UPSELL_GRID_SELECTOR}`);
      const titleEl = TITLE_SELECTORS.reduce(
        (found, selector) => found || card.querySelector(selector),
        null
      );
      const cardTitle = titleEl?.textContent.trim() || '';
      const productData = getSubscriptionProduct(card, cardTitle);

      return {
        card,
        grid,
        gridItem: getGridCardItem(card, grid),
        productData,
        isVisible: showAll || productMatchesFilter(productData, activeProductFilter),
      };
    });
    // The essentials grid and the upsell/add-on grid can render side-by-side
    // on the same builder step, each with its own product set. Scoping these
    // checks per grid (instead of globally across every card on the page)
    // stops a match in one grid from silencing the empty-state message for
    // the other grid that genuinely has zero matches.
    const gridStats = new Map();
    grids.forEach((grid) => {
      const statesForGrid = cardStates.filter(({ grid: cardGrid }) => cardGrid === grid);
      const hasFilterData = statesForGrid.some(
        ({ productData }) => getProductFilterValues(productData).length > 0
      );
      const hasMatches = statesForGrid.some(({ isVisible }) => isVisible);
      gridStats.set(grid, {
        shouldFallbackToShowAll: !showAll && !hasFilterData,
        hasMatches,
      });
    });

    const resolveVisibility = (grid, isVisible) =>
      gridStats.get(grid)?.shouldFallbackToShowAll ? true : isVisible;

    const emptyStateGrid = fallbackGrid || cardStates.find(({ grid }) => grid)?.grid || null;

    cardStates.forEach(({ card, gridItem, grid, isVisible }) => {
      const nextIsVisible = resolveVisibility(grid, isVisible);
      setFilterVisibility(gridItem, nextIsVisible);
      if (gridItem !== card) {
        setFilterVisibility(card, nextIsVisible);
      }
    });

    grids.forEach((grid) => {
      const hasVisibleCards = cardStates.some(({ grid: cardGrid, isVisible }) => {
        return cardGrid === grid && resolveVisibility(grid, isVisible);
      });

      grid.classList.remove(FILTER_EMPTY_GRID_CLASS);
      setFilterVisibility(grid, hasVisibleCards);
    });

    const showEmptyState =
      !showAll &&
      grids.some((grid) => {
        const stats = gridStats.get(grid);
        return Boolean(stats && !stats.shouldFallbackToShowAll && !stats.hasMatches);
      });

    const emptyState = ensureFilterEmptyState(emptyStateGrid);
    if (emptyState) {
      emptyState.hidden = !showEmptyState;
    }

    const anyGridFellBackToShowAll = grids.some(
      (grid) => gridStats.get(grid)?.shouldFallbackToShowAll
    );
    return !anyGridFellBackToShowAll;
  }

  function getSubscriptionProductsStore() {
    if (typeof subscriptionProducts === 'undefined') {
      return null;
    }

    if (!subscriptionProducts || typeof subscriptionProducts !== 'object') {
      return null;
    }

    return subscriptionProducts;
  }

  function getSubscriptionProduct(card, cardTitle) {
    const store = getSubscriptionProductsStore();
    if (!store) {
      return null;
    }

    if (store[cardTitle]) {
      return store[cardTitle];
    }

    const normalizedCardTitle = normalizeProductLookupKey(cardTitle);
    const titleMatch = Object.entries(store).find(
      ([title]) => normalizeProductLookupKey(title) === normalizedCardTitle
    );

    if (titleMatch) {
      return titleMatch[1];
    }

    const handle = getProductHandle(card, cardTitle);
    if (handle) {
      return (
        Object.values(store).find((product) => product.handle === handle) ||
        null
      );
    }

    return null;
  }

  function storeSubscriptionProduct(productData) {
    const store = getSubscriptionProductsStore();
    if (!store || !productData || typeof productData !== 'object') {
      return productData || null;
    }

    const existingEntry = Object.entries(store).find(
      ([title, product]) =>
        product?.handle === productData.handle ||
        title === productData.title ||
        normalizeProductLookupKey(title) === normalizeProductLookupKey(productData.title || '')
    );

    if (existingEntry) {
      const [, existingProduct] = existingEntry;
      Object.assign(existingProduct, productData);
      return existingProduct;
    }

    const key = productData.title || productData.handle;
    if (!key) {
      return null;
    }

    store[key] = productData;
    return store[key];
  }

  async function fetchSubscriptionProductData(handle) {
    if (!handle) {
      return null;
    }

    if (subscriptionProductFetchCache.has(handle)) {
      return subscriptionProductFetchCache.get(handle);
    }

    const request = (async () => {
      try {
        const response = await fetch(`/products/${handle}?view=subscription-data`);
        if (!response.ok) {
          return null;
        }

        const productData = await response.json();
        return storeSubscriptionProduct(productData);
      } catch (_) {
        return null;
      }
    })();

    subscriptionProductFetchCache.set(handle, request);
    return request;
  }

  async function resolveSubscriptionProduct(card, cardTitle) {
    const existing = getSubscriptionProduct(card, cardTitle);
    if (existing) {
      return existing;
    }

    const handle = await resolveProductHandle(card, cardTitle);
    return handle ? fetchSubscriptionProductData(handle) : null;
  }

  function syncProductCardTextBlock(card, className, text, anchorEl, { renderAsHtml = false } = {}) {
    let block = card.querySelector(`.${className}`);
    const normalizedText = String(text || '').trim();
    const normalizedContent = renderAsHtml ? stripHtml(normalizedText).trim() : normalizedText;

    if (!normalizedContent) {
      block?.remove();
      return anchorEl;
    }

    if (!block) {
      block = document.createElement('div');
      block.className = `${className} subscription-richtext`;
    }

    if (block.dataset.subscriptionText !== normalizedText || block.dataset.subscriptionRenderMode !== String(renderAsHtml)) {
      if (renderAsHtml) {
        block.innerHTML = normalizedText;
      } else {
        block.innerHTML = `<p>${escapeHtml(normalizedText)}</p>`;
      }
      block.dataset.subscriptionText = normalizedText;
      block.dataset.subscriptionRenderMode = String(renderAsHtml);
    }

    if (anchorEl?.nextElementSibling !== block) {
      anchorEl.insertAdjacentElement('afterend', block);
    }

    return block;
  }

  function syncProductCardDescriptions(card, titleEl, productData) {
    const shortText = productData?.small_description
      ? String(productData.small_description).trim()
      : '';
    const longText = productData?.long_description
      ? truncateText(stripHtml(productData.long_description), 96)
      : '';

    let lastInserted = titleEl;
    lastInserted = syncProductCardTextBlock(card, 'product-card-short-text', shortText, lastInserted, {
      renderAsHtml: true,
    });
    syncProductCardTextBlock(card, 'product-card-description', longText, lastInserted);
  }

  function getVariantSelectorLabel(product) {
    const optionName = product?.options
      ?.map((option) => {
        if (typeof option === 'string') {
          return option;
        }

        if (option && typeof option === 'object') {
          return option.name || option.label || option.title || '';
        }

        return '';
      })
      .find((optionName) => optionName && !/^title$/i.test(optionName));

    return optionName || 'Flavor';
  }

  function syncCardVariantLabel(card, labelText) {
    if (!card || !labelText) {
      return;
    }

    card.dataset.subscriptionVariantLabel = labelText;
    card.querySelectorAll('.product-card-variant-caption').forEach((caption) => {
      setProductCardVariantCaption(caption, labelText);
    });
  }

  function setProductCardVariantCaption(caption, labelText) {
    if (!caption) {
      return;
    }

    const marker = caption.querySelector('.product-card-required-marker');
    caption.textContent = labelText;
    if (marker) {
      caption.appendChild(document.createTextNode(' '));
      caption.appendChild(marker);
    }
  }

  function escapeRegExp(value) {
    return String(value || '').replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  function formatVariantOptionLabel(label, productTitle) {
    const cleanLabel = String(label || '').replace(/\s+/g, ' ').trim();
    const cleanTitle = String(productTitle || '').replace(/\s+/g, ' ').trim();

    if (!cleanLabel || !cleanTitle) {
      return cleanLabel;
    }

    const titlePrefixPattern = new RegExp(`^${escapeRegExp(cleanTitle)}\\s*[-:/]?\\s*`, 'i');
    const trimmedLabel = cleanLabel.replace(titlePrefixPattern, '').trim();

    return trimmedLabel || cleanLabel;
  }

  async function addProductDescription() {
    let shouldRefreshFilters = false;

    for (const selector of TITLE_SELECTORS) {
      for (const titleEl of document.querySelectorAll(selector)) {
        const card = findProductCard(titleEl);
        if (
          !card ||
          card.dataset.productDescriptionPending === 'true'
        ) {
          continue;
        }

        card.dataset.productDescriptionPending = 'true';

        try {
          const cardTitle = titleEl.textContent.trim();
          const hadPreloadedData = Boolean(getSubscriptionProduct(card, cardTitle));
          const productData = await resolveSubscriptionProduct(card, cardTitle);

          if (!titleEl.isConnected || !card.isConnected) {
            continue;
          }

          if (productData && !hadPreloadedData) {
            shouldRefreshFilters = true;
          }

          syncProductCardDescriptions(card, titleEl, productData);
        } finally {
          delete card.dataset.productDescriptionPending;
        }
      }
    }

    if (shouldRefreshFilters) {
      applyProductFilter();
    }
  }

  async function swapCardImages() {
    for (const selector of TITLE_SELECTORS) {
      for (const titleEl of document.querySelectorAll(selector)) {
        const stored = titleEl.dataset.lifecycleSrc;

        if (stored === 'pending' || stored === 'none') {
          continue;
        }

        const card = findProductCard(titleEl);
        if (!card) {
          continue;
        }

        if (stored) {
          // Already resolved — re-apply if Skio replaced the img element
          const imgEl = findCardImage(card);
          if (imgEl && !imgEl.dataset.lifecycleGuarded) {
            applyLifecycleImage(imgEl, stored);
          }
          continue;
        }

        // First time processing this card
        titleEl.dataset.lifecycleSrc = 'pending';

        const cardTitle = titleEl.textContent.trim();
        const handle = await resolveProductHandle(card, cardTitle);
        const lifecycleSrc = handle ? await fetchLifecycleImageForHandle(handle) : null;

        if (lifecycleSrc) {
          const imgEl = findCardImage(card);
          if (imgEl) {
            applyLifecycleImage(imgEl, lifecycleSrc);
          }
          titleEl.dataset.lifecycleSrc = lifecycleSrc;
        } else {
          titleEl.dataset.lifecycleSrc = 'none';
        }
      }
    }
  }

  function findCardImage(card) {
    return (
      card.querySelector('[data-skio-name="product-card-image"] img') ||
      card.querySelector('.product-card__image img, .card__image img, figure img') ||
      [...card.querySelectorAll('img')].find((el) => el.width > 50 || el.height > 50 || el.naturalWidth > 50)
    );
  }

  function applyLifecycleImage(imgEl, lifecycleSrc) {
    imgEl.src = lifecycleSrc;
    imgEl.srcset = '';
    imgEl.dataset.lifecycleGuarded = 'true';

    const normalized = lifecycleSrc.startsWith('//') ? `https:${lifecycleSrc}` : lifecycleSrc;
    const guard = new MutationObserver(() => {
      if (!imgEl.isConnected) {
        guard.disconnect();
        return;
      }
      if (imgEl.src !== normalized) {
        imgEl.src = lifecycleSrc;
        imgEl.srcset = '';
      }
    });
    guard.observe(imgEl, { attributes: true, attributeFilter: ['src', 'srcset'] });
  }

  async function fetchLifecycleImageForHandle(handle) {
    if (!handle) {
      return null;
    }

    if (window.__lifecycleImages && handle in window.__lifecycleImages) {
      return window.__lifecycleImages[handle] || null;
    }

    if (lifecycleImageFetchCache.has(handle)) {
      return lifecycleImageFetchCache.get(handle);
    }

    try {
      const response = await fetch(`/products/${handle}?view=lifecycle`);
      const text = response.ok ? (await response.text()).trim() : '';
      const isUrl = text.startsWith('//') || text.startsWith('https://') || text.startsWith('http://');
      const result = isUrl ? text : null;
      lifecycleImageFetchCache.set(handle, result);
      return result;
    } catch (_) {
      lifecycleImageFetchCache.set(handle, null);
      return null;
    }
  }

  function findProductCard(element) {
    const card = element.closest(SKIO_CARD_SELECTOR);
    if (card) {
      return card;
    }

    let node = element.parentElement;
    while (node && node !== document.body) {
      const hasTitle = node.querySelector('[data-skio-name="product-card-title"]');
      const hasAction =
        node.querySelector('[data-skio-name="product-card-add-button"]') ||
        node.querySelector('button') ||
        node.querySelector('select');

      if (hasTitle && hasAction) {
        return node;
      }

      node = node.parentElement;
    }

    return element.parentElement;
  }

  function ensureModal() {
    if (modal) {
      return;
    }

    modal = document.createElement('div');
    modal.className = 'subscription-details-modal';
    modal.innerHTML = `
      <div class="subscription-details-modal__overlay" data-subscription-modal-close></div>
      <div class="subscription-details-modal__dialog" role="dialog" aria-modal="true" aria-labelledby="subscription-details-modal-title">
        <div class="subscription-details-modal__content">
          <div class="subscription-details-modal__info">
            <h2 class="subscription-details-modal__title" id="subscription-details-modal-title"></h2>
            <div class="subscription-details-modal__body">
              <div class="subscription-details-modal__description subscription-richtext"></div>
              <div class="subscription-details-modal__facts" hidden>
                <button
                  type="button"
                  class="subscription-details-modal__facts-toggle"
                  aria-expanded="false"
                  aria-controls="subscription-details-modal-facts-panel"
                >
                  <span class="subscription-details-modal__facts-label">Supplement Facts</span>
                  <span class="subscription-details-modal__facts-icon" aria-hidden="true"></span>
                </button>
                <div class="subscription-details-modal__facts-panel" id="subscription-details-modal-facts-panel" hidden>
                  <img class="subscription-details-modal__nutrition-image" src="" alt="Supplement facts" loading="lazy">
                </div>
              </div>
            </div>
            <div class="subscription-details-modal__footer">
              <div class="subscription-details-modal__variant-wrap" hidden>
                <div class="subscription-details-modal__variant-label">Flavor</div>
                <select class="subscription-details-modal__variant" aria-label="Choose variant"></select>
              </div>
              <button type="button" class="subscription-details-modal__add-btn">Add</button>
            </div>
          </div>
          <div class="subscription-details-modal__media">
            <button type="button" class="subscription-details-modal__close" data-subscription-modal-close aria-label="Close">&times;</button>
            <img class="subscription-details-modal__image" src="" alt="">
          </div>
        </div>
      </div>
    `;

    document.body.appendChild(modal);

    modal.querySelector('.subscription-details-modal__variant').addEventListener('change', onVariantChange);
    modal.querySelector('.subscription-details-modal__add-btn').addEventListener('click', onAddToBox);
    modal.querySelector('.subscription-details-modal__facts-toggle').addEventListener('click', toggleNutritionFacts);
    modal.querySelectorAll('[data-subscription-modal-close]').forEach((el) => {
      el.addEventListener('click', closeModal);
    });
    modal
      .querySelector('.subscription-details-modal__description')
      .addEventListener('click', onDescriptionAccordionClick);

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && modal.classList.contains('is-open')) {
        closeModal();
      }
    });
  }

  function setModalDescription(descriptionEl, html) {
    descriptionEl.innerHTML = html;
    enhanceDescriptionAccordions(descriptionEl);
  }

  function toggleNutritionFacts() {
    if (!modal) {
      return;
    }

    const toggle = modal.querySelector('.subscription-details-modal__facts-toggle');
    const panel = modal.querySelector('.subscription-details-modal__facts-panel');
    const isExpanded = toggle?.getAttribute('aria-expanded') === 'true';

    if (!toggle || !panel) {
      return;
    }

    toggle.setAttribute('aria-expanded', String(!isExpanded));
    panel.hidden = isExpanded;
  }

  function buildModalContentHtml(subscriptionProduct) {
    const longDescription =
      typeof subscriptionProduct?.long_description === 'string'
        ? subscriptionProduct.long_description.trim()
        : '';

    return stripHtml(longDescription) ? longDescription : '';
  }

  function splitListItemContent(li) {
    const wrapper = document.createElement('div');
    wrapper.innerHTML = li.innerHTML.trim();

    let root = wrapper;
    if (wrapper.children.length === 1 && wrapper.firstElementChild?.tagName === 'P') {
      root = wrapper.firstElementChild;
    }

    const emphasis = root.querySelector(':scope > strong, :scope > b, strong, b');
    if (!emphasis) {
      return {
        title: wrapper.textContent.replace(/\s+/g, ' ').trim(),
        bodyHtml: '',
      };
    }

    const title = emphasis.textContent.replace(/\s+/g, ' ').trim();
    const bodyRoot = root.cloneNode(true);
    const emphasisInBody = bodyRoot.querySelector('strong, b');

    if (emphasisInBody) {
      emphasisInBody.remove();
    }

    const bodyHtml = bodyRoot.innerHTML.trim();
    const bodyText = bodyRoot.textContent.replace(/\s+/g, ' ').trim();

    return {
      title: title || wrapper.textContent.replace(/\s+/g, ' ').trim(),
      bodyHtml: bodyText ? bodyHtml : '',
    };
  }

  function enhanceDescriptionAccordions(container) {
    if (!container) {
      return;
    }

    container.querySelectorAll('ul').forEach((list) => {
      const listItems = [...list.querySelectorAll(':scope > li')];
      if (!listItems.length) {
        return;
      }

      const parsedItems = listItems.map((listItem) => splitListItemContent(listItem));
      const shouldAccordionize = parsedItems.some(({ bodyHtml }) => Boolean(bodyHtml));

      if (!shouldAccordionize) {
        return;
      }

      const accordion = document.createElement('div');
      accordion.className = 'subscription-description-accordion';

      parsedItems.forEach(({ title, bodyHtml }, index) => {
        const hasSubText = Boolean(bodyHtml);

        if (hasSubText) {
          const panelId = `subscription-desc-panel-${index}-${Math.random().toString(36).slice(2, 8)}`;
          const item = document.createElement('div');
          item.className = 'subscription-description-accordion__item';
          item.innerHTML = `
            <button
              type="button"
              class="subscription-description-accordion__trigger"
              aria-expanded="false"
              aria-controls="${panelId}"
            >
              <span class="subscription-description-accordion__label">
                <span class="subscription-description-accordion__bullet" aria-hidden="true"></span>
                <span class="subscription-description-accordion__title">${escapeHtml(title)}</span>
              </span>
              <span class="subscription-description-accordion__icon" aria-hidden="true"></span>
            </button>
            <div class="subscription-description-accordion__panel" id="${panelId}" hidden>
              <div class="subscription-description-accordion__content subscription-richtext">${bodyHtml}</div>
            </div>
          `;
          accordion.appendChild(item);
          return;
        }

        const item = document.createElement('div');
        item.className =
          'subscription-description-accordion__item subscription-description-accordion__item--static';
        item.innerHTML = `
          <div class="subscription-description-accordion__static">
            <span class="subscription-description-accordion__bullet" aria-hidden="true"></span>
            <span class="subscription-description-accordion__title">${escapeHtml(title)}</span>
          </div>
        `;
        accordion.appendChild(item);
      });

      list.replaceWith(accordion);
    });
  }

  function onDescriptionAccordionClick(event) {
    const trigger = event.target.closest('.subscription-description-accordion__trigger');
    if (!trigger) {
      return;
    }

    const isExpanded = trigger.getAttribute('aria-expanded') === 'true';
    const panelId = trigger.getAttribute('aria-controls');
    const panel = panelId
      ? trigger.closest('.subscription-details-modal__description')?.querySelector(`#${CSS.escape(panelId)}`)
      : null;

    trigger.setAttribute('aria-expanded', String(!isExpanded));

    if (panel) {
      panel.hidden = isExpanded;
    }
  }

  async function openModal(card) {
    if (!card || !modal) {
      return;
    }

    activeCard = card;

    const titleEl = modal.querySelector('.subscription-details-modal__title');
    const bodyEl = modal.querySelector('.subscription-details-modal__body');
    const descriptionEl = modal.querySelector('.subscription-details-modal__description');
    const imageEl = modal.querySelector('.subscription-details-modal__image');
    const variantSelect = modal.querySelector('.subscription-details-modal__variant');
    const variantWrap = modal.querySelector('.subscription-details-modal__variant-wrap');
    const factsWrap = modal.querySelector('.subscription-details-modal__facts');
    const factsToggle = modal.querySelector('.subscription-details-modal__facts-toggle');
    const factsPanel = modal.querySelector('.subscription-details-modal__facts-panel');
    const addButton = modal.querySelector('.subscription-details-modal__add-btn');

    if (bodyEl) {
      bodyEl.scrollTop = 0;
    }

    variantWrap.hidden = true;
    variantSelect.innerHTML = '';
    if (factsWrap) {
      factsWrap.hidden = true;
    }
    if (factsToggle) {
      factsToggle.setAttribute('aria-expanded', 'false');
    }
    if (factsPanel) {
      factsPanel.hidden = true;
    }

    const cardTitle =
      card.querySelector('[data-skio-name="product-card-title"]')?.textContent.trim() || 'Product';
    let subscriptionProduct = getSubscriptionProduct(card, cardTitle);

    if (addButton) {
      addButton.textContent = getCardAddButtonLabel(card);
    }

    titleEl.textContent = subscriptionProduct?.title || cardTitle;
    if (subscriptionProduct?.long_description) {
      setModalDescription(descriptionEl, subscriptionProduct.long_description);
    } else {
      descriptionEl.innerHTML = '';
    }

    const cardSelect = getCardVariantSelect(card);
    const initialVariantId = cardSelect?.value || syncDefaultCardVariant(card);
    updateNutritionImage(subscriptionProduct, initialVariantId, cardTitle);
    // Use lifecycle image already resolved by swapCardImages, or fall back to card image
    const cardTitleEl = TITLE_SELECTORS.reduce((found, sel) => found || card.querySelector(sel), null);
    const preResolvedSrc = cardTitleEl?.dataset.lifecycleSrc;
    if (preResolvedSrc && preResolvedSrc !== 'none' && preResolvedSrc !== 'pending') {
      imageEl.src = preResolvedSrc;
      imageEl.alt = cardTitle;
    } else {
      const cardImage = card.querySelector('[data-skio-name="product-card-image"] img, img');
      if (cardImage?.src) {
        imageEl.src = cardImage.src;
        imageEl.alt = cardImage.alt || cardTitle;
      }
    }

    modal.classList.add('is-open');
    document.documentElement.classList.add('overflow-hidden');

    try {
      const [product, resolvedSubscriptionProduct] = await Promise.all([
        resolveProduct(card, cardTitle),
        resolveSubscriptionProduct(card, cardTitle),
      ]);

      if (activeCard !== card) {
        return;
      }

      subscriptionProduct = resolvedSubscriptionProduct || subscriptionProduct;

      titleEl.textContent = subscriptionProduct?.title || product.title || cardTitle;
      setModalDescription(descriptionEl, buildModalContentHtml(subscriptionProduct));

      const lifecycleSrc = await fetchLifecycleImageForHandle(product.handle);
      const imageSrc = lifecycleSrc || product.featured_image || product.images?.[0];
      if (imageSrc) {
        imageEl.src = imageSrc;
        imageEl.alt = product.title;
      }

      populateVariantSelect(variantSelect, product, card, initialVariantId);
      updateNutritionImage(subscriptionProduct, variantSelect.value, cardTitle);
    } catch (error) {
      console.error('Subscription modal hydration failed for', cardTitle, error);
    }
  }

  function closeModal() {
    if (!modal) {
      return;
    }

    modal.classList.remove('is-open');
    document.documentElement.classList.remove('overflow-hidden');
    activeCard = null;
  }

  function onVariantChange(event) {
    if (!activeCard) {
      return;
    }

    const variantId = event.target.value;
    syncCardVariant(activeCard, variantId);

    const cardTitle = activeCard.querySelector('[data-skio-name="product-card-title"]')?.textContent.trim() || '';
    const subscriptionProduct = getSubscriptionProduct(activeCard, cardTitle);
    updateNutritionImage(subscriptionProduct, variantId, cardTitle);
  }

  function updateNutritionImage(subscriptionProduct, variantId, productTitle) {
    if (!modal) {
      return;
    }

    const factsWrap = modal.querySelector('.subscription-details-modal__facts');
    const factsToggle = modal.querySelector('.subscription-details-modal__facts-toggle');
    const factsPanel = modal.querySelector('.subscription-details-modal__facts-panel');
    const factsLabel = modal.querySelector('.subscription-details-modal__facts-label');
    const nutritionImage = modal.querySelector('.subscription-details-modal__nutrition-image');

    if (!factsWrap || !factsToggle || !factsPanel || !nutritionImage) {
      return;
    }

    const imageSrc = subscriptionProduct?.nutritional_images?.[String(variantId)] || null;

    if (imageSrc) {
      nutritionImage.src = imageSrc;
      nutritionImage.alt = `${productTitle || 'Product'} supplement facts`;
      if (factsLabel) {
        factsLabel.textContent = 'Supplement Facts';
      }
      factsWrap.hidden = false;
      factsPanel.hidden = true;
      factsToggle.setAttribute('aria-expanded', 'false');
    } else {
      nutritionImage.removeAttribute('src');
      factsWrap.hidden = true;
      factsPanel.hidden = true;
      factsToggle.setAttribute('aria-expanded', 'false');
    }
  }

  function onAddToBox() {
    if (!activeCard || !modal) {
      return;
    }

    const variantSelect = modal.querySelector('.subscription-details-modal__variant');
    if (variantSelect?.value) {
      syncCardVariant(activeCard, variantSelect.value);
    }

    const addButton = getCardAddButton(activeCard);

    if (addButton) {
      addButton.click();
      closeModal();
    }
  }

  function getCardAddButton(card) {
    if (!card) {
      return null;
    }

    return (
      card.querySelector('[data-skio-name="product-card-add-button"]') ||
      card.querySelector('[data-skio-name="add-to-box-button"]') ||
      [
        ...card.querySelectorAll('button'),
        ...card.querySelectorAll('input[type="button"], input[type="submit"]'),
      ].find((button) =>
        /add|box|cart|ritual/i.test(button.textContent || button.value || '')
      ) ||
      null
    );
  }

  function getCardAddButtonLabel(card) {
    const addButton = getCardAddButton(card);
    const textTarget = addButton?.querySelector('[data-text="true"]');
    const label = String(
      textTarget?.textContent ||
      addButton?.value ||
      addButton?.getAttribute('value') ||
      addButton?.textContent ||
      ''
    ).replace(/\s+/g, ' ').trim();

    return label || PRODUCT_CARD_ADD_LABEL;
  }

  function getCardVariantSelect(card) {
    if (!card) {
      return null;
    }

    return (
      card.querySelector('[data-skio-name="product-card-variant-select"] select') ||
      card.querySelector('select')
    );
  }

  function getFirstSelectableOption(select) {
    if (!select) {
      return null;
    }

    return [...select.options].find((option) => option.value && !option.disabled) || null;
  }

  function syncDefaultCardVariant(card) {
    const cardSelect = getCardVariantSelect(card);
    if (!cardSelect) {
      return null;
    }

    const currentOption = [...cardSelect.options].find(
      (option) => option.value === String(cardSelect.value) && !option.disabled
    );

    if (currentOption) {
      return currentOption.value;
    }

    const defaultOption = getFirstSelectableOption(cardSelect);
    if (!defaultOption) {
      return null;
    }

    cardSelect.value = defaultOption.value;
    cardSelect.dispatchEvent(new Event('change', { bubbles: true }));
    cardSelect.dispatchEvent(new Event('input', { bubbles: true }));
    return defaultOption.value;
  }

  function selectDefaultCardVariants() {
    document.querySelectorAll(SKIO_CARD_SELECTOR).forEach((card) => {
      syncDefaultCardVariant(card);
    });
  }

  function injectCardVariantSelectors() {
    document.querySelectorAll(SKIO_CARD_SELECTOR).forEach((card) => {
      if (card.dataset.variantSelectorAttached === 'true') {
        return;
      }

      const nativeSelect = getCardVariantSelect(card);
      if (!nativeSelect) {
        return;
      }

      const selectableOptions = [...nativeSelect.options].filter(
        (opt) => opt.value && !opt.disabled && opt.textContent.trim() !== 'Default Title'
      );

      if (selectableOptions.length <= 1) {
        return;
      }

      card.dataset.variantSelectorAttached = 'true';

      const wrap = document.createElement('div');
      wrap.className = 'product-card-variant-wrap';

      const noteId = `ProductCardVariantRequiredNote-${Date.now()}-${Math.random().toString(36).slice(2)}`;
      const requiredNote = document.createElement('p');
      requiredNote.className = 'product-card-required-note';
      requiredNote.id = noteId;
      requiredNote.innerHTML = '<span class="product-card-required-marker">*</span> indicates a required field.';
      wrap.appendChild(requiredNote);

      const caption = document.createElement('div');
      caption.className = 'product-card-variant-caption';
      const captionMarker = document.createElement('span');
      captionMarker.className = 'product-card-required-marker';
      captionMarker.textContent = '*';
      caption.appendChild(captionMarker);
      setProductCardVariantCaption(caption, card.dataset.subscriptionVariantLabel || 'Flavor');
      wrap.appendChild(caption);

      const trigger = document.createElement('button');
      trigger.className = 'product-card-variant-trigger';
      trigger.type = 'button';
      trigger.setAttribute('aria-haspopup', 'listbox');
      trigger.setAttribute('aria-expanded', 'false');
      trigger.setAttribute('aria-describedby', noteId);

      const label = document.createElement('span');
      label.className = 'product-card-variant-label';

      const popup = document.createElement('div');
      popup.className = 'product-card-variant-popup';
      popup.setAttribute('role', 'listbox');
      popup.hidden = true;
      document.body.appendChild(popup);

      const titleEl = TITLE_SELECTORS.reduce(
        (found, selector) => found || card.querySelector(selector),
        null
      );
      const cardTitle = titleEl?.textContent.trim() || '';

      function getSelectedLabel() {
        const selected = selectableOptions.find((o) => o.value === nativeSelect.value);
        const fallbackLabel = selectableOptions[0]?.textContent.trim() || '';
        const selectedLabel = selected ? selected.textContent.trim() : fallbackLabel;
        return formatVariantOptionLabel(selectedLabel, cardTitle);
      }

      function updateOptions() {
        popup.querySelectorAll('.product-card-variant-option').forEach((btn) => {
          const isSelected = btn.dataset.value === nativeSelect.value;
          btn.classList.toggle('is-selected', isSelected);
          btn.querySelector('.product-card-variant-option-check').textContent = isSelected ? '✓' : '';
        });
        label.textContent = getSelectedLabel();
      }

      function positionPopup() {
        const rect = trigger.getBoundingClientRect();
        popup.style.top = (rect.bottom + 4) + 'px';
        popup.style.left = rect.left + 'px';
        popup.style.width = rect.width + 'px';
      }

      function closePopup() {
        popup.hidden = true;
        wrap.classList.remove('is-open');
        trigger.setAttribute('aria-expanded', 'false');
      }

      popup._close = closePopup;

      selectableOptions.forEach((opt) => {
        const btn = document.createElement('button');
        btn.className = 'product-card-variant-option';
        btn.type = 'button';
        btn.dataset.value = opt.value;
        btn.setAttribute('role', 'option');

        const check = document.createElement('span');
        check.className = 'product-card-variant-option-check';
        btn.appendChild(check);
        btn.appendChild(document.createTextNode(
          formatVariantOptionLabel(opt.textContent.trim(), cardTitle)
        ));

        btn.addEventListener('click', () => {
          syncCardVariant(card, opt.value);
          closePopup();
        });

        popup.appendChild(btn);
      });

      label.textContent = getSelectedLabel();
      trigger.appendChild(label);

      trigger.addEventListener('click', (e) => {
        e.stopPropagation();
        const isOpen = !popup.hidden;
        document.querySelectorAll('.product-card-variant-popup').forEach((p) => {
          if (p._close) p._close();
        });
        if (!isOpen) {
          positionPopup();
          popup.hidden = false;
          wrap.classList.add('is-open');
          trigger.setAttribute('aria-expanded', 'true');
        }
      });

      nativeSelect.addEventListener('change', () => {
        updateOptions();
        scheduleSkioEnhancements();
      });

      wrap.appendChild(trigger);

      const buttonContainer = card.querySelector('[data-skio-name="product-card-button-container"]');
      if (buttonContainer) {
        buttonContainer.insertAdjacentElement('beforebegin', wrap);
      } else {
        const details = card.querySelector('[data-skio-name="product-card-details"]');
        if (details) {
          details.appendChild(wrap);
        }
      }

      updateOptions();
    });
  }

  document.addEventListener('click', () => {
    document.querySelectorAll('.product-card-variant-popup:not([hidden])').forEach((p) => {
      if (p._close) p._close();
    });
  });

  window.addEventListener('scroll', (e) => {
    if (e.target && e.target.classList && e.target.classList.contains('product-card-variant-popup')) {
      return;
    }
    document.querySelectorAll('.product-card-variant-popup:not([hidden])').forEach((p) => {
      if (p._close) p._close();
    });
  }, { passive: true, capture: true });

  function normalizeVariantId(value) {
    // Handles plain numeric IDs ("12345") and Shopify GID format
    // ("gid://shopify/ProductVariant/12345") by extracting the trailing digits.
    return String(value || '').split('/').pop();
  }

  function syncCardVariant(card, variantId) {
    const cardSelect = getCardVariantSelect(card);

    if (!cardSelect) {
      return;
    }

    const normalizedId = normalizeVariantId(variantId);
    const option = [...cardSelect.options].find(
      (entry) => normalizeVariantId(entry.value) === normalizedId
    );

    if (option) {
      cardSelect.value = option.value;
      cardSelect.dispatchEvent(new Event('change', { bubbles: true }));
      cardSelect.dispatchEvent(new Event('input', { bubbles: true }));
    }
  }

  function populateVariantSelect(select, product, card, preferredVariantId) {
    const variantWrap = select.parentElement;
    const variantLabel = variantWrap?.querySelector('.subscription-details-modal__variant-label');
    select.innerHTML = '';

    const variants = product.variants || [];
    const cardTitle =
      card.querySelector('[data-skio-name="product-card-title"]')?.textContent.trim() || product.title;

    if (!variants.length) {
      variantWrap.hidden = true;
      return;
    }

    const hasMultipleVariants =
      variants.length > 1 &&
      variants.some((variant) => variant.title !== 'Default Title');

    variantWrap.hidden = !hasMultipleVariants;
    if (variantLabel) {
      variantLabel.textContent = getVariantSelectorLabel(product);
    }
    syncCardVariantLabel(card, getVariantSelectorLabel(product));

    variants.forEach((variant) => {
      const option = document.createElement('option');
      option.value = variant.id;
      option.textContent =
        variants.length > 1 && variant.title !== 'Default Title'
          ? formatVariantOptionLabel(variant.title, cardTitle)
          : cardTitle;
      select.appendChild(option);
    });

    const cardSelect = getCardVariantSelect(card);
    const rawId = preferredVariantId || cardSelect?.value || syncDefaultCardVariant(card);

    if (rawId) {
      const normalizedId = normalizeVariantId(rawId);
      const matchedOption = [...select.options].find(
        (o) => normalizeVariantId(o.value) === normalizedId
      );
      if (matchedOption) {
        select.value = matchedOption.value;
      }
    }

    if (!select.value) {
      select.value = String(variants[0].id);
      syncCardVariant(card, select.value);
    }
  }

  async function fetchProduct(handle) {
    if (productCache.has(handle)) {
      const cached = productCache.get(handle);
      if (cached === null) throw new Error('Product fetch failed');
      return cached;
    }

    const response = await fetch(`/products/${handle}.js`);
    if (!response.ok) {
      productCache.set(handle, null);
      throw new Error('Product fetch failed');
    }

    const product = await response.json();
    productCache.set(handle, product);
    return product;
  }

  async function searchProductHandle(title) {
    const response = await fetch(
      `/search/suggest.json?q=${encodeURIComponent(title)}&resources[type]=product&resources[limit]=1`
    );

    if (!response.ok) {
      return null;
    }

    const data = await response.json();
    return data.resources?.results?.products?.[0]?.handle || null;
  }

  async function resolveProduct(card, cardTitle) {
    const handle = await resolveProductHandle(card, cardTitle);
    if (!handle) {
      throw new Error('Product not found');
    }

    return fetchProduct(handle);
  }

  function getExplicitProductHandle(card) {
    const explicitHandle =
      card.getAttribute('data-product-handle') ||
      card.querySelector('[data-product-handle]')?.dataset.productHandle;

    if (explicitHandle) {
      return explicitHandle;
    }

    const productLink = card.querySelector('a[href*="/products/"]');
    if (productLink) {
      const match = productLink.href.match(/\/products\/([^/?#]+)/);
      if (match) {
        return match[1];
      }
    }

    return null;
  }

  function getProductHandle(card, cardTitle) {
    return getExplicitProductHandle(card) || slugify(cardTitle);
  }

  async function resolveProductHandle(card, cardTitle) {
    const explicitHandle = getExplicitProductHandle(card);
    if (explicitHandle) {
      return explicitHandle;
    }

    const cacheKey = normalizeProductLookupKey(cardTitle);
    if (!cacheKey) {
      return slugify(cardTitle);
    }

    if (handleResolutionCache.has(cacheKey)) {
      return handleResolutionCache.get(cacheKey);
    }

    const resolution = (async () => {
      const searchedHandle = await searchProductHandle(cardTitle).catch(() => null);
      return searchedHandle || slugify(cardTitle);
    })();

    handleResolutionCache.set(cacheKey, resolution);
    return resolution;
  }

  function parseProductContent(html) {
    const doc = new DOMParser().parseFromString(html || '', 'text/html');
    const paragraphs = [...doc.querySelectorAll('p')];
    let description = '';

    for (const paragraph of paragraphs) {
      const text = paragraph.textContent.trim();
      if (text && !/^(benefits|key features|includes):/i.test(text)) {
        description = text;
        break;
      }
    }

    let includes = [];
    const labels = [...doc.querySelectorAll('strong, p strong, p > span strong')];

    for (const label of labels) {
      const text = label.textContent.trim().replace(/:$/, '');
      if (!/key features|includes/i.test(text)) {
        continue;
      }

      let list = label.closest('p')?.nextElementSibling;
      if (!list || list.tagName !== 'UL') {
        list = label.parentElement?.nextElementSibling;
      }

      if (list?.tagName === 'UL') {
        includes = [...list.querySelectorAll('li')].map((item) =>
          item.textContent.trim().replace(/\s+/g, ' ')
        );
        break;
      }
    }

    if (!includes.length) {
      const lists = doc.querySelectorAll('ul');
      if (lists.length > 1) {
        includes = [...lists[1].querySelectorAll('li')].map((item) =>
          item.textContent.trim().replace(/\s+/g, ' ')
        );
      }
    }

    return { description, includes };
  }

  function stripHtml(html) {
    const doc = new DOMParser().parseFromString(html || '', 'text/html');
    return doc.body.textContent.replace(/\s+/g, ' ').trim();
  }

  function truncateText(value, maxLength) {
    const text = String(value || '').replace(/\s+/g, ' ').trim();

    if (text.length <= maxLength) {
      return text;
    }

    return `${text.slice(0, Math.max(0, maxLength - 1)).trimEnd()}…`;
  }

  function slugify(value) {
    const cleanMatch = value.match(/clean\s*(\d+)/i);
    if (cleanMatch) {
      return `clean${cleanMatch[1]}`;
    }

    return value
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  function escapeHtml(value) {
    return value
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function hideCartAddonDefaultTitles() {
    const cart = document.querySelector(SKIO_CART_SELECTOR);
    if (!cart) return;

    cart.querySelectorAll('*').forEach((el) => {
      if (!el.children.length && el.textContent.trim() === 'Default Title') {
        el.style.display = 'none';
      }
    });
  }

  function ensureAddonSectionBelow() {
    const cart = document.querySelector(SKIO_CART_SELECTOR);
    if (!cart) return;

    const variantsList = cart.querySelector('[data-skio-name="bab-cart-variants-list"]');
    const addonSection = cart.querySelector('[data-skio-name="bab-cart-addons"]');

    if (!variantsList || !addonSection) return;

    // Move addonSection to be a direct sibling after variantsList regardless of current nesting
    const variantsParent = variantsList.parentElement;
    if (!variantsParent) return;

    if (addonSection.parentElement !== variantsParent) {
      variantsList.insertAdjacentElement('afterend', addonSection);
    } else {
      const siblings = [...variantsParent.children];
      const variantsIdx = siblings.indexOf(variantsList);
      const addonIdx = siblings.indexOf(addonSection);
      if (addonIdx !== variantsIdx + 1) {
        variantsList.insertAdjacentElement('afterend', addonSection);
      }
    }

    // Force parent to wrap so addonSection drops to a new row below variantsList
    variantsParent.style.setProperty('display', 'flex', 'important');
    variantsParent.style.setProperty('flex-wrap', 'wrap', 'important');
    addonSection.style.setProperty('width', '100%', 'important');
    addonSection.style.setProperty('flex-basis', '100%', 'important');
  }

  function buildCartPlaceholder(slotIndex, discountSteps, purchaseMode) {
    const placeholder = document.createElement('div');
    placeholder.setAttribute('data-subscription-cart-placeholder', 'true');
    placeholder.className = 'subscription-cart-placeholder';

    const badge = document.createElement('div');
    badge.className = 'subscription-cart-placeholder__badge';
    const discountLabel =
      discountSteps.length > 0
        ? getDiscountLabelForSlot(purchaseMode, slotIndex)
        : '';
    badge.textContent = discountLabel;
    badge.hidden = !discountLabel;
    placeholder.appendChild(badge);

    const inner = document.createElement('div');
    inner.className = 'subscription-cart-placeholder__inner';
    placeholder.appendChild(inner);

    return placeholder;
  }

  function syncCartPlaceholders() {
    document.querySelectorAll('[data-skio-name="bab-cart-variants-list"]').forEach((list) => {
      const realItems = [...list.querySelectorAll('[data-skio-name="bab-cart-variant-item"]')];
      // Match getBoxItemCount(): tiers are quantity-based, so a slot is
      // "filled" per unit ordered, not per distinct line item — otherwise
      // bumping quantity on one product leaves stale placeholder squares.
      const realCount = realItems.reduce((total, item) => total + getCartVariantQuantity(item), 0);
      const purchaseMode = getSelectedPurchaseMode();
      const discountSteps = getConfiguredDiscountSteps(purchaseMode);
      // The bundle only has as many meaningful slots as there are discount tiers
      // (e.g. 3, matching "1 product / 2 products / 3+ products"). A 4th slot would
      // just repeat the last tier's discount, so cap total slots at discountSteps.length.
      const totalSlots = discountSteps.length > 0 ? discountSteps.length : CART_MAX_SLOTS;
      const targetCount = Math.max(0, totalSlots - realCount);
      const upsellSection = list.querySelector('[data-skio-name="bab-cart-upsell-section"]');

      // Remove excess placeholders
      const allPlaceholders = [...list.querySelectorAll('[data-subscription-cart-placeholder="true"]')];
      if (allPlaceholders.length > targetCount) {
        allPlaceholders.slice(targetCount).forEach((el) => el.remove());
      }

      // Add missing placeholders
      let placeholders = [...list.querySelectorAll('[data-subscription-cart-placeholder="true"]')];
      for (let i = placeholders.length; i < targetCount; i++) {
        const placeholder = buildCartPlaceholder(realCount + i, discountSteps, purchaseMode);
        if (upsellSection) {
          list.insertBefore(placeholder, upsellSection);
        } else {
          list.appendChild(placeholder);
        }
      }

      // Ensure placeholders always follow all real items (Skio may append items after our placeholders)
      placeholders = [...list.querySelectorAll('[data-subscription-cart-placeholder="true"]')];
      const lastRealItem = realItems[realItems.length - 1];
      if (lastRealItem && placeholders.length > 0) {
        const listChildren = [...list.children];
        const lastRealIdx = listChildren.indexOf(lastRealItem);
        const firstPlaceholderIdx = listChildren.indexOf(placeholders[0]);
        if (firstPlaceholderIdx !== -1 && firstPlaceholderIdx < lastRealIdx) {
          let anchor = lastRealItem;
          placeholders.forEach((ph) => {
            anchor.after(ph);
            anchor = ph;
          });
        }
      }

      // Update badge text and visibility
      [...list.querySelectorAll('[data-subscription-cart-placeholder="true"]')].forEach((placeholder, i) => {
        const slotIndex = realCount + i;
        const discountLabel =
          discountSteps.length > 0
            ? getDiscountLabelForSlot(purchaseMode, slotIndex)
            : '';
        const badge = placeholder.querySelector('.subscription-cart-placeholder__badge');
        if (badge) {
          if (badge.textContent !== discountLabel) badge.textContent = discountLabel;
          if (badge.hidden) badge.hidden = false;
          badge.dataset.skioDiscountEmpty = discountLabel ? 'false' : 'true';
        }
      });
    });
  }

  // Watch for Skio's interval-select dropdown portal (rendered via React Portal outside
  // #skio-root / #skio-portal), which means our CSS descendant selectors can't reach it.
  // Inline styles applied here beat all stylesheet !important declarations.
  function watchSkioIntervalSelectDropdown() {
    const OPTION_BG = '#fff';
    const OPTION_COLOR = '#2A6553';
    const OPTION_HOVER_BG = '#edf5e8';

    function styleListbox(listbox) {
      listbox.style.setProperty('background', OPTION_BG, 'important');
      listbox.style.setProperty('border-radius', '2px', 'important');
      listbox.style.setProperty('overflow', 'hidden', 'important');

      listbox.querySelectorAll('[role="option"]').forEach((option) => {
        option.style.setProperty('background', OPTION_BG, 'important');
        option.style.setProperty('color', OPTION_COLOR, 'important');
        option.style.setProperty('-webkit-text-fill-color', OPTION_COLOR, 'important');

        option.addEventListener('mouseenter', function () {
          this.style.setProperty('background', OPTION_HOVER_BG, 'important');
        });
        option.addEventListener('mouseleave', function () {
          this.style.setProperty('background', OPTION_BG, 'important');
        });
      });
    }

    function isSkioListbox(listbox) {
      if (listbox.classList.contains('product-card-variant-popup')) return false;
      return (
        !!listbox.closest('[data-headlessui-portal]') ||
        !!listbox.closest('#skio-root, #skio-portal')
      );
    }

    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (node.nodeType !== Node.ELEMENT_NODE) continue;

          const candidates = [];
          if (node.getAttribute('role') === 'listbox') candidates.push(node);
          node.querySelectorAll('[role="listbox"]').forEach((lb) => candidates.push(lb));

          for (const listbox of candidates) {
            if (isSkioListbox(listbox)) styleListbox(listbox);
          }
        }
      }
    });

    observer.observe(document.body, { childList: true, subtree: true });
  }

  watchSkioIntervalSelectDropdown();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
